import { BUILDING, BUILDING_SECTIONS, FOOTPRINT_OUTER, WALKWAY_OUTLINE, STAIR_OUTLINE, STAIR_NUM_STEPS, setStairNumSteps, GROUND_FLOOR, UPPER_FLOOR, ELEMENT_TYPES, PARKING_GROUPS, PLACED_PLANTERS, PLACED_ROADS, GROUND_PAD_OUTLINE, ITEM_GROUPS } from './building-data.js';

/**
 * 2D Floor Plan Editor — HTML5 Canvas-based drag & resize editor
 * with polygon shape support, element type management, and vertex editing.
 */
export class FloorPlanEditor {
  constructor({ onApply }) {
    this.onApply = onApply;

    // Deep-clone editable data
    this.data = {
      ground: {
        showrooms: JSON.parse(JSON.stringify(GROUND_FLOOR.showrooms)),
        specialAreas: JSON.parse(JSON.stringify(GROUND_FLOOR.specialAreas)),
      },
      upper: {
        showrooms: JSON.parse(JSON.stringify(UPPER_FLOOR.showrooms)),
        offices: JSON.parse(JSON.stringify(UPPER_FLOOR.offices)),
      },
    };

    this.activeFloor = 'ground'; // 'ground' | 'upper'
    this.selected = null;        // { list: 'showrooms'|'specialAreas'|'offices', index: number }
    this.hoverHandle = -1;       // -1 = none, 0-7 = handle index (rect), or vertex index (polygon)
    this.dragging = false;
    this.resizing = false;
    this.dragStart = null;
    this.dragOriginal = null;

    // Pan & zoom
    this.pan = { x: 144, y: 95 }; // center on building
    this.zoom = 2.5;
    this.panning = false;
    this.panStart = null;
    this.panOriginal = null;

    this.snapEnabled = true;
    this.snapSize = 0.5;

    // Outline editing
    this.outlineVertices = JSON.parse(JSON.stringify(FOOTPRINT_OUTER));
    this.editingOutline = false;
    this.selectedOutlineVertex = -1;
    this.draggingOutlineVertex = false;

    // Walkway editing
    this.walkwayVertices = JSON.parse(JSON.stringify(WALKWAY_OUTLINE));
    this.editingWalkway = false;
    this.selectedWalkwayVertex = -1;
    this.draggingWalkwayVertex = false;

    // Stair editing
    this.stairVertices = JSON.parse(JSON.stringify(STAIR_OUTLINE));
    this.stairNumSteps = STAIR_NUM_STEPS;
    this.editingStair = false;
    this.selectedStairVertex = -1;
    this.draggingStairVertex = false;

    // Ground pad editing
    this.groundPadVertices = JSON.parse(JSON.stringify(GROUND_PAD_OUTLINE));
    this.editingGroundPad = false;
    this.selectedGroundPadVertex = -1;
    this.draggingGroundPadVertex = false;

    // Parking groups editing
    this.parkingGroups = JSON.parse(JSON.stringify(PARKING_GROUPS));
    this.editingParking = false;
    this.selectedParkingIndex = -1;
    this.draggingParking = false;
    this.draggingParkingVertex = -1;
    this.parkingDragOriginal = null;

    // Planters editing
    this.planters = JSON.parse(JSON.stringify(PLACED_PLANTERS));
    this.editingPlanters = false;
    this.selectedPlanterIndex = -1;
    this.draggingPlanter = false;
    this.resizingPlanter = false;

    // Roads editing
    this.roads = JSON.parse(JSON.stringify(PLACED_ROADS));
    this.editingRoads = false;
    this.selectedRoadIndex = -1;
    this.draggingRoad = false;
    this.draggingRoadPoint = -1;

    // Alignment guides
    this.alignGuides = [];

    // Measurement hit areas (rebuilt each frame)
    this.measurementHitAreas = [];
    this._measurementInput = null; // active HTML input overlay

    // Clipboard for copy/paste
    this.clipboard = null;

    // Multi-selection (Ctrl+click to toggle items)
    this.multiSelected = [];  // array of { list, index }

    // Marquee (Shift+drag area select)
    this.marquee = null;       // { startX, startZ, endX, endZ } in building coords
    this.marqueeScreen = null; // { x1, y1, x2, y2 } in screen coords

    // Edge drag (for outline/walkway/stair/groundPad editing modes)
    this.draggingEdge = false;
    this.edgeDragTarget = null;        // 'outline' | 'walkway' | 'stair' | 'groundPad'
    this.edgeDragNormal = null;        // { nx, nz }
    this.edgeDragOriginalVerts = null; // deep copy of vertices at drag start

    // Groups
    this.groups = [];

    // Undo/redo history
    this.history = [];
    this.historyIndex = -1;
    this.maxHistory = 50;
    this._historyPaused = false;

    this.container = null;
    this.canvas = null;
    this.ctx = null;
    this.panel = null;
    this._raf = null;
    this._bound = {};
  }

  // ─── OPEN / CLOSE ───

  open() {
    this._buildUI();
    this._bindEvents();
    this._saveSnapshot(); // initial state
    this._render();
  }

  close() {
    if (this._raf) cancelAnimationFrame(this._raf);
    this._unbindEvents();
    if (this.container) {
      this.container.style.display = 'none';
      this.container.innerHTML = '';
    }
  }

  // ─── HELPERS ───

  _computeAABB(vertices) {
    let minX = Infinity, minZ = Infinity, maxX = -Infinity, maxZ = -Infinity;
    for (const v of vertices) {
      if (v.x < minX) minX = v.x;
      if (v.z < minZ) minZ = v.z;
      if (v.x > maxX) maxX = v.x;
      if (v.z > maxZ) maxZ = v.z;
    }
    return { x: minX, z: minZ, w: maxX - minX, d: maxZ - minZ };
  }

  _polygonArea(vertices) {
    // Shoelace formula
    let area = 0;
    const n = vertices.length;
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      area += vertices[i].x * vertices[j].z;
      area -= vertices[j].x * vertices[i].z;
    }
    return Math.abs(area) / 2;
  }

  _pointInPolygon(px, pz, vertices) {
    // Ray-casting algorithm
    let inside = false;
    const n = vertices.length;
    for (let i = 0, j = n - 1; i < n; j = i++) {
      const xi = vertices[i].x, zi = vertices[i].z;
      const xj = vertices[j].x, zj = vertices[j].z;
      if ((zi > pz) !== (zj > pz) &&
          px < (xj - xi) * (pz - zi) / (zj - zi) + xi) {
        inside = !inside;
      }
    }
    return inside;
  }

  _pointToSegmentDist(px, pz, ax, az, bx, bz) {
    const dx = bx - ax, dz = bz - az;
    const lenSq = dx * dx + dz * dz;
    if (lenSq === 0) return Math.hypot(px - ax, pz - az);
    let t = ((px - ax) * dx + (pz - az) * dz) / lenSq;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(px - (ax + t * dx), pz - (az + t * dz));
  }

  _hitTestEdge(vertices, bldX, bldZ, threshold) {
    for (let i = 0; i < vertices.length; i++) {
      const j = (i + 1) % vertices.length;
      const dist = this._pointToSegmentDist(bldX, bldZ,
        vertices[i].x, vertices[i].z, vertices[j].x, vertices[j].z);
      if (dist < threshold) {
        const edx = vertices[j].x - vertices[i].x;
        const edz = vertices[j].z - vertices[i].z;
        const len = Math.hypot(edx, edz);
        const nx = -edz / len, nz = edx / len;
        return { edgeIndex: i, nx, nz };
      }
    }
    return null;
  }

  _getAllSelected() {
    const result = [];
    if (this.selected) result.push(this.selected);
    result.push(...this.multiSelected);
    return result;
  }

  _nextGroupId() {
    let max = 0;
    this.groups.forEach(g => {
      const num = parseInt(g.id.split('-')[1]) || 0;
      if (num > max) max = num;
    });
    return `GRP-${max + 1}`;
  }

  _findGroupContaining(sel) {
    return this.groups.find(g => g.members.some(m =>
      m.type === 'item' && m.list === sel.list && m.index === sel.index
    ));
  }

  _getGroupMembers(group) {
    const items = [];
    for (const m of group.members) {
      if (m.type === 'item') {
        items.push(m);
      } else if (m.type === 'group') {
        const subGroup = this.groups.find(g => g.id === m.groupId);
        if (subGroup) items.push(...this._getGroupMembers(subGroup));
      }
    }
    return items;
  }

  _groupSelected() {
    const allSel = this._getAllSelected();
    if (allSel.length < 2) return;

    const id = this._nextGroupId();
    const members = allSel.map(sel => {
      const existingGroup = this._findGroupContaining(sel);
      if (existingGroup) {
        return { type: 'group', groupId: existingGroup.id };
      }
      return { type: 'item', list: sel.list, index: sel.index };
    });

    const seen = new Set();
    const uniqueMembers = members.filter(m => {
      const key = m.type === 'group' ? m.groupId : `${m.list}:${m.index}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    this.groups.push({ id, label: `Group ${id.split('-')[1]}`, members: uniqueMembers, collapsed: false });
    this._saveSnapshot();
    this._updatePanel();
    this._updateToolbarButtons();
  }

  _ungroupSelected() {
    if (!this.selected) return;
    const group = this._findGroupContaining(this.selected);
    if (!group) return;

    const parentGroup = this.groups.find(g => g.members.some(m => m.type === 'group' && m.groupId === group.id));
    if (parentGroup) {
      const idx = parentGroup.members.findIndex(m => m.type === 'group' && m.groupId === group.id);
      parentGroup.members.splice(idx, 1, ...group.members);
    }
    this.groups = this.groups.filter(g => g.id !== group.id);
    this._saveSnapshot();
    this._updatePanel();
    this._updateToolbarButtons();
  }

  _nextId(type) {
    const prefix = type === 'Showroom' ? 'SR' : type === 'Office' ? 'OF' :
                   type === 'Passage' ? 'PS' : type === 'WC' ? 'WC' :
                   type === 'Lift' ? 'LF' : type === 'Stairs' ? 'ST' :
                   type === 'Parking' ? 'PK' : type === 'AC Duct' ? 'AC' :
                   type === 'Void' ? 'VD' : 'EL';
    // Find highest existing number
    let max = 0;
    const allItems = this._getItems();
    allItems.forEach(item => {
      if (item.id && item.id.startsWith(prefix)) {
        const num = parseInt(item.id.slice(prefix.length + 1)) || 0;
        if (num > max) max = num;
      }
    });
    return `${prefix}-${max + 1}`;
  }

  // ─── UNDO / REDO ───

  _saveSnapshot() {
    if (this._historyPaused) return;
    const snap = JSON.stringify({
      ground: this.data.ground,
      upper: this.data.upper,
      outline: this.outlineVertices,
      walkway: this.walkwayVertices,
      stair: this.stairVertices,
      stairNumSteps: this.stairNumSteps,
      groundPad: this.groundPadVertices,
      parkingGroups: this.parkingGroups,
      planters: this.planters,
      roads: this.roads,
      groups: this.groups,
    });
    // Trim future states if we're in the middle of history
    if (this.historyIndex < this.history.length - 1) {
      this.history.splice(this.historyIndex + 1);
    }
    this.history.push(snap);
    if (this.history.length > this.maxHistory) {
      this.history.shift();
    }
    this.historyIndex = this.history.length - 1;
    this._updateUndoRedoButtons();
  }

  _undo() {
    if (this.historyIndex <= 0) return;
    this.historyIndex--;
    this._restoreSnapshot(this.history[this.historyIndex]);
  }

  _redo() {
    if (this.historyIndex >= this.history.length - 1) return;
    this.historyIndex++;
    this._restoreSnapshot(this.history[this.historyIndex]);
  }

  _restoreSnapshot(snap) {
    const state = JSON.parse(snap);
    this.data.ground = state.ground;
    this.data.upper = state.upper;
    if (state.outline) this.outlineVertices = state.outline;
    if (state.walkway) this.walkwayVertices = state.walkway;
    if (state.stair) this.stairVertices = state.stair;
    if (state.stairNumSteps != null) this.stairNumSteps = state.stairNumSteps;
    if (state.groundPad) this.groundPadVertices = state.groundPad;
    if (state.parkingGroups) this.parkingGroups = state.parkingGroups;
    if (state.planters) this.planters = state.planters;
    if (state.roads) this.roads = state.roads;
    if (state.groups) this.groups = state.groups;
    this.selected = null;
    this.multiSelected = [];
    this.selectedOutlineVertex = -1;
    this.selectedWalkwayVertex = -1;
    this.selectedStairVertex = -1;
    this.selectedGroundPadVertex = -1;
    this.selectedParkingIndex = -1;
    this.selectedPlanterIndex = -1;
    this.selectedRoadIndex = -1;
    this._updatePanel();
    this._updateToolbarButtons();
    this._updateUndoRedoButtons();
  }

  _updateUndoRedoButtons() {
    const undoBtn = document.getElementById('ed-undo');
    const redoBtn = document.getElementById('ed-redo');
    if (undoBtn) undoBtn.disabled = this.historyIndex <= 0;
    if (redoBtn) redoBtn.disabled = this.historyIndex >= this.history.length - 1;
  }

  // ─── COPY / PASTE ───

  _copySelected() {
    if (!this.selected) return;
    const item = this._getSelectedItem();
    if (!item) return;
    this.clipboard = JSON.parse(JSON.stringify(item));
    // Remove internal keys
    delete this.clipboard._list;
    delete this.clipboard._index;
    this._flashButton('ed-copy', 'Copied!');
  }

  _pasteClipboard() {
    if (!this.clipboard) return;
    const copy = JSON.parse(JSON.stringify(this.clipboard));
    // Offset to avoid exact overlap
    const offset = 5;
    copy.x = (copy.x || 0) + offset;
    copy.z = (copy.z || 0) + offset;
    if (copy.vertices) {
      copy.vertices.forEach(v => { v.x += offset; v.z += offset; });
    }
    // Generate new unique ID
    copy.id = this._nextId(copy.type || 'Showroom');
    copy.label = copy.label ? copy.label + ' (copy)' : copy.id;

    const list = this.activeFloor === 'ground' ? this.data.ground : this.data.upper;
    const listName = this._getListForType(copy.type);
    list[listName].push(copy);
    this.selected = { list: listName, index: list[listName].length - 1 };

    this._saveSnapshot();
    this._updatePanel();
    this._updateToolbarButtons();
  }

  _getListForType(type) {
    if (this.activeFloor === 'ground') {
      if (type === 'Showroom') return 'showrooms';
      return 'specialAreas';
    } else {
      if (type === 'Office') return 'offices';
      return 'showrooms';
    }
  }

  _flashButton(id, text) {
    const btn = document.getElementById(id);
    if (!btn) return;
    const orig = btn.textContent;
    btn.textContent = text;
    setTimeout(() => { btn.textContent = orig; }, 1000);
  }

  // ─── UI CONSTRUCTION ───

  _buildUI() {
    this.container = document.getElementById('editor-container');
    this.container.style.display = 'flex';
    this.container.innerHTML = '';

    // Build type options for dropdown
    const typeOptions = Object.keys(ELEMENT_TYPES)
      .filter(t => t !== 'Special Area')
      .map(t => `<option value="${t}">${t}</option>`)
      .join('');

    // Main layout: toolbar + canvas + property panel
    this.container.innerHTML = `
      <div id="editor-toolbar">
        <button class="ed-btn ed-tab active" data-floor="ground">Ground Floor</button>
        <button class="ed-btn ed-tab" data-floor="upper">Upper Floor</button>
        <span class="ed-sep"></span>
        <select class="ed-select" id="ed-add-type">
          <option value="" disabled selected>+ Add...</option>
          ${typeOptions}
          <option disabled>───────</option>
          <option value="_parking">Parking Group</option>
          <option value="_planter">Planter</option>
          <option value="_road">Road</option>
        </select>
        <button class="ed-btn ed-action" id="ed-delete" disabled>Delete</button>
        <button class="ed-btn ed-action" id="ed-toggle-poly" disabled>To Polygon</button>
        <span class="ed-sep"></span>
        <button class="ed-btn ed-action" id="ed-copy" disabled title="Ctrl+C">Copy</button>
        <button class="ed-btn ed-action" id="ed-paste" disabled title="Ctrl+V">Paste</button>
        <button class="ed-btn ed-action" id="ed-undo" disabled title="Ctrl+Z">Undo</button>
        <button class="ed-btn ed-action" id="ed-redo" disabled title="Ctrl+Y">Redo</button>
        <span class="ed-sep"></span>
        <select class="ed-select" id="ed-edit-mode">
          <option value="" selected>Edit Mode...</option>
          <option value="outline">Outline</option>
          <option value="walkway">Walkway</option>
          <option value="stair">Stairs</option>
          <option value="groundPad">Ground Pad</option>
          <option value="parking">Parking</option>
          <option value="planters">Planters</option>
          <option value="roads">Roads</option>
        </select>
        <button class="ed-btn ed-action" id="ed-exit-mode" style="display:none">Done</button>
        <span class="ed-sep"></span>
        <label class="ed-snap"><input type="checkbox" id="ed-snap-toggle" checked /> Snap</label>
        <span class="ed-sep"></span>
        <button class="ed-btn ed-action" id="ed-group" disabled>Group</button>
        <button class="ed-btn ed-action" id="ed-ungroup" disabled>Ungroup</button>
        <span class="ed-sep"></span>
        <button class="ed-btn ed-action" id="ed-apply">Apply to 3D</button>
        <button class="ed-btn ed-action" id="ed-export">Export</button>
        <button class="ed-btn ed-close" id="ed-close">Close</button>
      </div>
      <!-- Hidden buttons for compatibility with existing code -->
      <div style="display:none">
        <button id="ed-outline"></button>
        <button id="ed-walkway"></button>
        <button id="ed-stair"></button>
        <button id="ed-ground-pad"></button>
        <button id="ed-parking"></button>
        <button id="ed-planters"></button>
        <button id="ed-roads"></button>
        <button id="ed-add-parking"></button>
        <button id="ed-add-planter"></button>
        <button id="ed-add-road"></button>
        <button id="ed-delete-ext"></button>
      </div>
      <div id="editor-main">
        <canvas id="editor-canvas"></canvas>
        <div id="editor-panel">
          <h3>Properties</h3>
          <div id="ed-props-content"><p class="ed-hint">Click a unit to select it</p></div>
        </div>
      </div>
    `;

    this.canvas = document.getElementById('editor-canvas');
    this.ctx = this.canvas.getContext('2d');
    this.panel = document.getElementById('ed-props-content');

    this._resizeCanvas();
  }

  _resizeCanvas() {
    const main = document.getElementById('editor-main');
    if (!main || !this.canvas) return;
    const rect = main.getBoundingClientRect();
    const panelW = 260;
    this.canvas.width = rect.width - panelW;
    this.canvas.height = rect.height;
  }

  // ─── COORDINATE TRANSFORMS ───

  _toScreen(bx, bz) {
    const cx = this.canvas.width / 2;
    const cy = this.canvas.height / 2;
    return {
      x: (bx - this.pan.x) * this.zoom + cx,
      y: (bz - this.pan.y) * this.zoom + cy,
    };
  }

  _toBuilding(sx, sy) {
    const cx = this.canvas.width / 2;
    const cy = this.canvas.height / 2;
    return {
      x: (sx - cx) / this.zoom + this.pan.x,
      z: (sy - cy) / this.zoom + this.pan.y,
    };
  }

  // ─── GET ACTIVE ITEMS ───

  _getItems() {
    const items = [];
    if (this.activeFloor === 'ground') {
      this.data.ground.showrooms.forEach((s, i) =>
        items.push({ ...s, _list: 'showrooms', _index: i }));
      this.data.ground.specialAreas.forEach((s, i) =>
        items.push({ ...s, _list: 'specialAreas', _index: i }));
    } else {
      this.data.upper.showrooms.forEach((s, i) =>
        items.push({ ...s, _list: 'showrooms', _index: i }));
      this.data.upper.offices.forEach((s, i) =>
        items.push({ ...s, _list: 'offices', _index: i }));
    }
    return items;
  }

  _getItemRef(listName, index) {
    if (this.activeFloor === 'ground') {
      return this.data.ground[listName][index];
    } else {
      return this.data.upper[listName][index];
    }
  }

  _getSelectedItem() {
    if (!this.selected) return null;
    return this._getItemRef(this.selected.list, this.selected.index);
  }

  _isPolygon(item) {
    return item && Array.isArray(item.vertices) && item.vertices.length >= 3;
  }

  // ─── COLORS ───

  _colorForItem(item) {
    const t = item.type || item._type;
    if (t && ELEMENT_TYPES[t]) return ELEMENT_TYPES[t].color;
    // Legacy fallback
    switch (t) {
      case 'showroom': return '#2a6b3a';
      case 'specialArea': return '#4a3a6b';
      case 'office': return '#3a5a6b';
      default: return '#555';
    }
  }

  // ─── RENDERING ───

  _render() {
    const ctx = this.ctx;
    const W = this.canvas.width;
    const H = this.canvas.height;

    ctx.clearRect(0, 0, W, H);
    this.measurementHitAreas = [];

    // Background
    ctx.fillStyle = '#1a1a2e';
    ctx.fillRect(0, 0, W, H);

    // Grid
    this._drawGrid();

    // Building outline
    this._drawBuildingOutline();

    // Walkway polygon
    this._drawWalkwayOutline();

    // Stair polygon
    this._drawStairOutline();

    // Ground pad polygon
    this._drawGroundPadOutline();

    // Placed roads (drawn before parking/planters so they appear underneath)
    this._drawPlacedRoads();

    // Parking groups
    this._drawParkingGroups();

    // Placed planters
    this._drawPlacedPlanters();

    // All items
    const items = this._getItems();
    items.forEach(item => {
      const isSelected = (this.selected &&
        item._list === this.selected.list &&
        item._index === this.selected.index) ||
        this.multiSelected.some(s => s.list === item._list && s.index === item._index);

      if (this._isPolygon(item)) {
        this._drawPolygon(item, isSelected);
      } else {
        this._drawRect(item, isSelected);
      }
    });

    // Handles for selected
    if (this.selected) {
      const sel = this._getSelectedItem();
      if (sel) {
        if (this._isPolygon(sel)) {
          this._drawVertexHandles(sel);
        } else {
          this._drawHandles(sel);
        }
      }
    }

    // Live dimension overlay during drag/resize
    if ((this.dragging || this.resizing) && this.selected) {
      const sel = this._getSelectedItem();
      if (sel) this._drawDimOverlay(sel);
    }

    // Alignment guides
    this._drawAlignGuides();

    // Group outlines
    this.groups.forEach(group => {
      const members = this._getGroupMembers(group);
      if (members.length === 0) return;

      let gMinX = Infinity, gMinZ = Infinity, gMaxX = -Infinity, gMaxZ = -Infinity;
      for (const m of members) {
        const item = this._getItemRef(m.list, m.index);
        if (!item) continue;
        if (this._isPolygon(item)) {
          const aabb = this._computeAABB(item.vertices);
          gMinX = Math.min(gMinX, aabb.x); gMinZ = Math.min(gMinZ, aabb.z);
          gMaxX = Math.max(gMaxX, aabb.x + aabb.w); gMaxZ = Math.max(gMaxZ, aabb.z + aabb.d);
        } else {
          gMinX = Math.min(gMinX, item.x); gMinZ = Math.min(gMinZ, item.z);
          gMaxX = Math.max(gMaxX, item.x + item.w); gMaxZ = Math.max(gMaxZ, item.z + item.d);
        }
      }

      if (gMinX === Infinity) return;
      const gtl = this._toScreen(gMinX - 1, gMinZ - 1);
      const gbr = this._toScreen(gMaxX + 1, gMaxZ + 1);
      ctx.strokeStyle = '#ff8844';
      ctx.setLineDash([6, 3]);
      ctx.lineWidth = 1.5;
      ctx.strokeRect(gtl.x, gtl.y, gbr.x - gtl.x, gbr.y - gtl.y);
      ctx.setLineDash([]);

      ctx.fillStyle = '#ff8844';
      ctx.font = '10px sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'bottom';
      ctx.fillText(group.label, gtl.x + 2, gtl.y - 3);
    });

    // Marquee selection rectangle
    if (this.marqueeScreen) {
      const { x1, y1, x2, y2 } = this.marqueeScreen;
      ctx.strokeStyle = '#4488ff';
      ctx.fillStyle = 'rgba(68, 136, 255, 0.15)';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 3]);
      const rx = Math.min(x1, x2), ry = Math.min(y1, y2);
      const rw = Math.abs(x2 - x1), rh = Math.abs(y2 - y1);
      ctx.fillRect(rx, ry, rw, rh);
      ctx.strokeRect(rx, ry, rw, rh);
      ctx.setLineDash([]);
    }

    this._raf = requestAnimationFrame(() => this._render());
  }

  _drawGrid() {
    const ctx = this.ctx;
    let gridStep = 10;
    if (this.zoom > 4) gridStep = 5;
    if (this.zoom > 8) gridStep = 1;

    const tl = this._toBuilding(0, 0);
    const br = this._toBuilding(this.canvas.width, this.canvas.height);

    const startX = Math.floor(tl.x / gridStep) * gridStep;
    const startZ = Math.floor(tl.z / gridStep) * gridStep;

    ctx.strokeStyle = 'rgba(255,255,255,0.06)';
    ctx.lineWidth = 0.5;

    for (let gx = startX; gx <= br.x; gx += gridStep) {
      const s = this._toScreen(gx, 0);
      ctx.beginPath();
      ctx.moveTo(s.x, 0);
      ctx.lineTo(s.x, this.canvas.height);
      ctx.stroke();
    }
    for (let gz = startZ; gz <= br.z; gz += gridStep) {
      const s = this._toScreen(0, gz);
      ctx.beginPath();
      ctx.moveTo(0, s.y);
      ctx.lineTo(this.canvas.width, s.y);
      ctx.stroke();
    }

    // Major grid (50ft) slightly brighter
    ctx.strokeStyle = 'rgba(255,255,255,0.12)';
    ctx.lineWidth = 1;
    const majorStep = 50;
    const majorStartX = Math.floor(tl.x / majorStep) * majorStep;
    const majorStartZ = Math.floor(tl.z / majorStep) * majorStep;
    for (let gx = majorStartX; gx <= br.x; gx += majorStep) {
      const s = this._toScreen(gx, 0);
      ctx.beginPath();
      ctx.moveTo(s.x, 0);
      ctx.lineTo(s.x, this.canvas.height);
      ctx.stroke();
    }
    for (let gz = majorStartZ; gz <= br.z; gz += majorStep) {
      const s = this._toScreen(0, gz);
      ctx.beginPath();
      ctx.moveTo(0, s.y);
      ctx.lineTo(this.canvas.width, s.y);
      ctx.stroke();
    }
  }

  _drawBuildingOutline() {
    const ctx = this.ctx;
    const verts = this.outlineVertices;

    // Draw outline polygon from editable vertices
    ctx.strokeStyle = this.editingOutline ? '#ffcc00' : '#c9a94e';
    ctx.lineWidth = this.editingOutline ? 3 : 2;
    ctx.setLineDash([]);
    ctx.beginPath();
    const p0 = this._toScreen(verts[0].x, verts[0].z);
    ctx.moveTo(p0.x, p0.y);
    for (let i = 1; i < verts.length; i++) {
      const p = this._toScreen(verts[i].x, verts[i].z);
      ctx.lineTo(p.x, p.y);
    }
    ctx.closePath();
    ctx.stroke();

    // Semi-transparent fill
    ctx.fillStyle = this.editingOutline ? 'rgba(255,204,0,0.08)' : 'rgba(201,169,78,0.08)';
    ctx.fill();

    // Section overlays
    ctx.fillStyle = 'rgba(201,169,78,0.15)';
    BUILDING_SECTIONS.forEach(sec => {
      const tl = this._toScreen(sec.x, sec.z);
      const sz = { x: sec.w * this.zoom, y: sec.d * this.zoom };
      ctx.fillRect(tl.x, tl.y, sz.x, sz.y);
    });

    // Edge measurements when editing outline
    if (this.editingOutline) {
      this._drawEdgeMeasurements(verts, true, '#ffcc00', 14, { type: 'outline', verts: this.outlineVertices });
    }

    // Vertex handles when editing outline
    if (this.editingOutline) {
      const radius = 7;
      for (let i = 0; i < verts.length; i++) {
        const s = this._toScreen(verts[i].x, verts[i].z);
        ctx.beginPath();
        ctx.arc(s.x, s.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = i === this.selectedOutlineVertex ? '#fff' : '#ffcc00';
        ctx.fill();
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        // Vertex index label
        ctx.fillStyle = '#000';
        ctx.font = 'bold 9px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(i.toString(), s.x, s.y);
      }

      // Show coordinates of selected vertex
      if (this.selectedOutlineVertex >= 0) {
        const sv = verts[this.selectedOutlineVertex];
        const ss = this._toScreen(sv.x, sv.z);
        const label = `(${sv.x.toFixed(1)}, ${sv.z.toFixed(1)})`;
        ctx.fillStyle = 'rgba(0,0,0,0.85)';
        ctx.font = 'bold 12px sans-serif';
        const tw = ctx.measureText(label).width;
        ctx.fillRect(ss.x - tw / 2 - 4, ss.y - 26, tw + 8, 18);
        ctx.fillStyle = '#ffcc00';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, ss.x, ss.y - 17);
      }
    }
  }

  _drawWalkwayOutline() {
    const ctx = this.ctx;
    const verts = this.walkwayVertices;
    if (!verts || verts.length < 3) return;

    // Draw walkway polygon
    ctx.strokeStyle = this.editingWalkway ? '#00ccff' : '#4a9ec9';
    ctx.lineWidth = this.editingWalkway ? 3 : 2;
    ctx.setLineDash(this.editingWalkway ? [] : [6, 4]);
    ctx.beginPath();
    const p0 = this._toScreen(verts[0].x, verts[0].z);
    ctx.moveTo(p0.x, p0.y);
    for (let i = 1; i < verts.length; i++) {
      const p = this._toScreen(verts[i].x, verts[i].z);
      ctx.lineTo(p.x, p.y);
    }
    ctx.closePath();
    ctx.stroke();
    ctx.setLineDash([]);

    // Semi-transparent fill
    ctx.fillStyle = this.editingWalkway ? 'rgba(0,204,255,0.1)' : 'rgba(74,158,201,0.06)';
    ctx.fill();

    // Edge measurements when editing walkway
    if (this.editingWalkway) {
      this._drawEdgeMeasurements(verts, true, '#00ccff', 14, { type: 'walkway', verts: this.walkwayVertices });
    }

    // Vertex handles when editing walkway
    if (this.editingWalkway) {
      const radius = 7;
      for (let i = 0; i < verts.length; i++) {
        const s = this._toScreen(verts[i].x, verts[i].z);
        ctx.beginPath();
        ctx.arc(s.x, s.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = i === this.selectedWalkwayVertex ? '#fff' : '#00ccff';
        ctx.fill();
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        // Vertex index label
        ctx.fillStyle = '#000';
        ctx.font = 'bold 9px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(i.toString(), s.x, s.y);
      }

      // Show coordinates of selected vertex
      if (this.selectedWalkwayVertex >= 0) {
        const sv = verts[this.selectedWalkwayVertex];
        const ss = this._toScreen(sv.x, sv.z);
        const label = `(${sv.x.toFixed(1)}, ${sv.z.toFixed(1)})`;
        ctx.fillStyle = 'rgba(0,0,0,0.85)';
        ctx.font = 'bold 12px sans-serif';
        const tw = ctx.measureText(label).width;
        ctx.fillRect(ss.x - tw / 2 - 4, ss.y - 26, tw + 8, 18);
        ctx.fillStyle = '#00ccff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, ss.x, ss.y - 17);
      }
    }
  }

  _drawStairOutline() {
    const ctx = this.ctx;
    const verts = this.stairVertices;
    if (!verts || verts.length < 3) return;

    ctx.strokeStyle = this.editingStair ? '#ff8844' : '#9e6a4a';
    ctx.lineWidth = this.editingStair ? 3 : 2;
    ctx.setLineDash(this.editingStair ? [] : [6, 4]);
    ctx.beginPath();
    const p0 = this._toScreen(verts[0].x, verts[0].z);
    ctx.moveTo(p0.x, p0.y);
    for (let i = 1; i < verts.length; i++) {
      const p = this._toScreen(verts[i].x, verts[i].z);
      ctx.lineTo(p.x, p.y);
    }
    ctx.closePath();
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.fillStyle = this.editingStair ? 'rgba(255,136,68,0.1)' : 'rgba(158,106,74,0.06)';
    ctx.fill();

    // Edge measurements when editing stair
    if (this.editingStair) {
      this._drawEdgeMeasurements(verts, true, '#ff8844', 14, { type: 'stair', verts: this.stairVertices });
    }

    // Draw step lines when editing
    if (this.editingStair && verts.length >= 4) {
      const iS = verts[0], iN = verts[1], oN = verts[2], oS = verts[3];
      const numSteps = this.stairNumSteps;
      ctx.strokeStyle = 'rgba(255,136,68,0.4)';
      ctx.lineWidth = 1;
      for (let s = 1; s < numSteps; s++) {
        const t = s / numSteps;
        const lx = iS.x + (oS.x - iS.x) * t;
        const lz = iS.z + (oS.z - iS.z) * t;
        const rx = iN.x + (oN.x - iN.x) * t;
        const rz = iN.z + (oN.z - iN.z) * t;
        const sl = this._toScreen(lx, lz);
        const sr = this._toScreen(rx, rz);
        ctx.beginPath();
        ctx.moveTo(sl.x, sl.y);
        ctx.lineTo(sr.x, sr.y);
        ctx.stroke();
      }
    }

    if (this.editingStair) {
      const radius = 7;
      for (let i = 0; i < verts.length; i++) {
        const s = this._toScreen(verts[i].x, verts[i].z);
        ctx.beginPath();
        ctx.arc(s.x, s.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = i === this.selectedStairVertex ? '#fff' : '#ff8844';
        ctx.fill();
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        ctx.fillStyle = '#000';
        ctx.font = 'bold 9px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(i.toString(), s.x, s.y);
      }

      if (this.selectedStairVertex >= 0) {
        const sv = verts[this.selectedStairVertex];
        const ss = this._toScreen(sv.x, sv.z);
        const label = `(${sv.x.toFixed(1)}, ${sv.z.toFixed(1)})`;
        ctx.fillStyle = 'rgba(0,0,0,0.85)';
        ctx.font = 'bold 12px sans-serif';
        const tw = ctx.measureText(label).width;
        ctx.fillRect(ss.x - tw / 2 - 4, ss.y - 26, tw + 8, 18);
        ctx.fillStyle = '#ff8844';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, ss.x, ss.y - 17);
      }
    }
  }

  _drawGroundPadOutline() {
    const ctx = this.ctx;
    const verts = this.groundPadVertices;
    if (!verts || verts.length < 3) return;

    ctx.strokeStyle = this.editingGroundPad ? '#44cc44' : '#2a8a2a';
    ctx.lineWidth = this.editingGroundPad ? 3 : 2;
    ctx.setLineDash(this.editingGroundPad ? [] : [6, 4]);
    ctx.beginPath();
    const p0 = this._toScreen(verts[0].x, verts[0].z);
    ctx.moveTo(p0.x, p0.y);
    for (let i = 1; i < verts.length; i++) {
      const p = this._toScreen(verts[i].x, verts[i].z);
      ctx.lineTo(p.x, p.y);
    }
    ctx.closePath();
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.fillStyle = this.editingGroundPad ? 'rgba(68,204,68,0.12)' : 'rgba(42,138,42,0.06)';
    ctx.fill();

    // Edge measurements when editing ground pad
    if (this.editingGroundPad) {
      this._drawEdgeMeasurements(verts, true, '#44cc44', 14, { type: 'groundPad', verts: this.groundPadVertices });
    }

    if (this.editingGroundPad) {
      const radius = 7;
      for (let i = 0; i < verts.length; i++) {
        const s = this._toScreen(verts[i].x, verts[i].z);
        ctx.beginPath();
        ctx.arc(s.x, s.y, radius, 0, Math.PI * 2);
        ctx.fillStyle = i === this.selectedGroundPadVertex ? '#fff' : '#44cc44';
        ctx.fill();
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 1.5;
        ctx.stroke();

        ctx.fillStyle = '#000';
        ctx.font = 'bold 9px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(i.toString(), s.x, s.y);
      }

      if (this.selectedGroundPadVertex >= 0) {
        const sv = verts[this.selectedGroundPadVertex];
        const ss = this._toScreen(sv.x, sv.z);
        const label = `(${sv.x.toFixed(1)}, ${sv.z.toFixed(1)})`;
        ctx.fillStyle = 'rgba(0,0,0,0.85)';
        ctx.font = 'bold 12px sans-serif';
        const tw = ctx.measureText(label).width;
        ctx.fillRect(ss.x - tw / 2 - 4, ss.y - 26, tw + 8, 18);
        ctx.fillStyle = '#44cc44';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, ss.x, ss.y - 17);
      }
    }
  }

  _drawParkingGroups() {
    const ctx = this.ctx;
    this.parkingGroups.forEach((grp, gi) => {
      const verts = grp.vertices;
      if (!verts || verts.length < 3) return;
      const isSelected = this.editingParking && gi === this.selectedParkingIndex;

      // Fill
      ctx.globalAlpha = 0.4;
      ctx.fillStyle = isSelected ? '#6688aa' : '#4a5a6a';
      ctx.beginPath();
      const p0 = this._toScreen(verts[0].x, verts[0].z);
      ctx.moveTo(p0.x, p0.y);
      for (let i = 1; i < verts.length; i++) {
        const p = this._toScreen(verts[i].x, verts[i].z);
        ctx.lineTo(p.x, p.y);
      }
      ctx.closePath();
      ctx.fill();
      ctx.globalAlpha = 1.0;

      // Border
      ctx.strokeStyle = isSelected ? '#88bbdd' : (this.editingParking ? '#5a7a9a' : 'rgba(90,122,154,0.4)');
      ctx.lineWidth = isSelected ? 2.5 : (this.editingParking ? 1.5 : 1);
      ctx.setLineDash(isSelected ? [6, 3] : []);
      ctx.beginPath();
      ctx.moveTo(p0.x, p0.y);
      for (let i = 1; i < verts.length; i++) {
        const p = this._toScreen(verts[i].x, verts[i].z);
        ctx.lineTo(p.x, p.y);
      }
      ctx.closePath();
      ctx.stroke();
      ctx.setLineDash([]);

      // Parking lines when editing
      if (this.editingParking) {
        this._drawParkingLines(grp);
      }

      // Label
      const aabb = this._computeAABB(verts);
      const cx = aabb.x + aabb.w / 2;
      const cz = aabb.z + aabb.d / 2;
      const sc = this._toScreen(cx, cz);
      const labelText = grp.label || grp.id;
      ctx.fillStyle = '#ccc';
      ctx.font = 'bold 10px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(labelText, sc.x, sc.y);

      // Edge measurements when selected
      if (isSelected) {
        this._drawEdgeMeasurements(verts, true, '#88bbdd', 16, { type: 'parking', index: gi, verts });
      }

      // Vertex handles when selected
      if (isSelected) {
        const radius = 6;
        for (let i = 0; i < verts.length; i++) {
          const s = this._toScreen(verts[i].x, verts[i].z);
          ctx.beginPath();
          ctx.arc(s.x, s.y, radius, 0, Math.PI * 2);
          ctx.fillStyle = '#88bbdd';
          ctx.fill();
          ctx.strokeStyle = '#000';
          ctx.lineWidth = 1;
          ctx.stroke();
        }
      }
    });
  }

  _drawParkingLines(grp) {
    const ctx = this.ctx;
    const verts = grp.vertices;
    if (!verts || verts.length < 4) return;

    // Edge v0→v3 and v1→v2 (short edges = along diagonal)
    const groupDx = verts[3].x - verts[0].x;
    const groupDz = verts[3].z - verts[0].z;
    const groupWidth = Math.hypot(groupDx, groupDz);
    const spotWidth = grp.spotWidth || 9;

    ctx.strokeStyle = 'rgba(255,255,255,0.3)';
    ctx.lineWidth = 1;
    for (let ld = spotWidth; ld < groupWidth; ld += spotWidth) {
      const t = ld / groupWidth;
      const ax = verts[0].x + t * groupDx;
      const az = verts[0].z + t * groupDz;
      const bx = verts[1].x + t * (verts[2].x - verts[1].x);
      const bz = verts[1].z + t * (verts[2].z - verts[1].z);
      const sa = this._toScreen(ax, az);
      const sb = this._toScreen(bx, bz);
      ctx.beginPath();
      ctx.moveTo(sa.x, sa.y);
      ctx.lineTo(sb.x, sb.y);
      ctx.stroke();
    }
  }

  _drawPlacedPlanters() {
    const ctx = this.ctx;
    this.planters.forEach((pl, i) => {
      const isSelected = this.editingPlanters && i === this.selectedPlanterIndex;
      const sc = this._toScreen(pl.x, pl.z);
      const isRect = (pl.shape || 'round') === 'rect';

      ctx.globalAlpha = 0.5;
      ctx.fillStyle = isSelected ? '#55cc55' : '#2a8a2a';

      if (isRect) {
        const w = (pl.width || pl.diameter || 5) * this.zoom;
        const d = (pl.depth || pl.diameter || 5) * this.zoom;
        const ang = (pl.angle || 0) * Math.PI / 180;
        ctx.save();
        ctx.translate(sc.x, sc.y);
        ctx.rotate(-ang);
        ctx.fillRect(-w / 2, -d / 2, w, d);
        ctx.globalAlpha = 1.0;
        ctx.strokeStyle = isSelected ? '#88ee88' : (this.editingPlanters ? '#44aa44' : 'rgba(42,138,42,0.4)');
        ctx.lineWidth = isSelected ? 2.5 : 1;
        ctx.setLineDash(isSelected ? [4, 2] : []);
        ctx.strokeRect(-w / 2, -d / 2, w, d);
        ctx.setLineDash([]);

        // Tree dot
        if (pl.hasTree) {
          ctx.fillStyle = '#1a5a1a';
          ctx.beginPath();
          ctx.arc(0, 0, Math.max(Math.min(w, d) * 0.15, 3), 0, Math.PI * 2);
          ctx.fill();
        }

        // Resize handle (corner) when selected
        if (isSelected) {
          ctx.fillStyle = '#88ee88';
          ctx.fillRect(w / 2 - 4, d / 2 - 4, 8, 8);
          ctx.strokeStyle = '#000';
          ctx.lineWidth = 1;
          ctx.strokeRect(w / 2 - 4, d / 2 - 4, 8, 8);
        }

        ctx.restore();
      } else {
        const r = (pl.diameter || 5) / 2 * this.zoom;
        ctx.beginPath();
        ctx.arc(sc.x, sc.y, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1.0;

        // Border
        ctx.strokeStyle = isSelected ? '#88ee88' : (this.editingPlanters ? '#44aa44' : 'rgba(42,138,42,0.4)');
        ctx.lineWidth = isSelected ? 2.5 : 1;
        ctx.setLineDash(isSelected ? [4, 2] : []);
        ctx.beginPath();
        ctx.arc(sc.x, sc.y, r, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);

        // Tree dot
        if (pl.hasTree) {
          ctx.fillStyle = '#1a5a1a';
          ctx.beginPath();
          ctx.arc(sc.x, sc.y, Math.max(r * 0.3, 3), 0, Math.PI * 2);
          ctx.fill();
        }

        // Resize handle when selected
        if (isSelected) {
          const hx = sc.x + r;
          const hy = sc.y;
          ctx.fillStyle = '#88ee88';
          ctx.fillRect(hx - 4, hy - 4, 8, 8);
          ctx.strokeStyle = '#000';
          ctx.lineWidth = 1;
          ctx.strokeRect(hx - 4, hy - 4, 8, 8);
        }
      }

      // Measurements when selected
      if (isSelected) {
        this._drawPlanterMeasurements(pl, i);
      }

      // Label
      if (this.editingPlanters) {
        const labelText = pl.label || pl.id;
        ctx.fillStyle = '#ccc';
        ctx.font = '9px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        const labelOffset = isRect
          ? Math.max((pl.depth || pl.diameter || 5) / 2 * this.zoom, (pl.width || pl.diameter || 5) / 2 * this.zoom) + 3
          : (pl.diameter || 5) / 2 * this.zoom + 3;
        ctx.fillText(labelText, sc.x, sc.y + labelOffset);
      }
    });
  }

  _drawRect(item, isSelected) {
    const ctx = this.ctx;
    const tl = this._toScreen(item.x, item.z);
    const sw = item.w * this.zoom;
    const sh = item.d * this.zoom;

    // Fill
    ctx.globalAlpha = 0.5;
    ctx.fillStyle = this._colorForItem(item);
    ctx.fillRect(tl.x, tl.y, sw, sh);
    ctx.globalAlpha = 1.0;

    // Border
    if (isSelected) {
      ctx.strokeStyle = '#c9a94e';
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 3]);
    } else {
      ctx.strokeStyle = 'rgba(255,255,255,0.4)';
      ctx.lineWidth = 1;
      ctx.setLineDash([]);
    }
    ctx.strokeRect(tl.x, tl.y, sw, sh);
    ctx.setLineDash([]);

    // Label
    const labelText = item.label || item.id || '';
    const maxFontSize = Math.min(sw * 0.8 / Math.max(labelText.length * 0.55, 1), sh * 0.35, 14);
    if (maxFontSize >= 5) {
      ctx.fillStyle = '#fff';
      ctx.font = `bold ${Math.round(maxFontSize)}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(labelText, tl.x + sw / 2, tl.y + sh / 2, sw - 4);
    }
  }

  _drawPolygon(item, isSelected) {
    const ctx = this.ctx;
    const verts = item.vertices;
    if (!verts || verts.length < 3) return;

    // Fill
    ctx.globalAlpha = 0.5;
    ctx.fillStyle = this._colorForItem(item);
    ctx.beginPath();
    const p0 = this._toScreen(verts[0].x, verts[0].z);
    ctx.moveTo(p0.x, p0.y);
    for (let i = 1; i < verts.length; i++) {
      const p = this._toScreen(verts[i].x, verts[i].z);
      ctx.lineTo(p.x, p.y);
    }
    ctx.closePath();
    ctx.fill();
    ctx.globalAlpha = 1.0;

    // Border
    if (isSelected) {
      ctx.strokeStyle = '#c9a94e';
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 3]);
    } else {
      ctx.strokeStyle = 'rgba(255,255,255,0.4)';
      ctx.lineWidth = 1;
      ctx.setLineDash([]);
    }
    ctx.beginPath();
    ctx.moveTo(p0.x, p0.y);
    for (let i = 1; i < verts.length; i++) {
      const p = this._toScreen(verts[i].x, verts[i].z);
      ctx.lineTo(p.x, p.y);
    }
    ctx.closePath();
    ctx.stroke();
    ctx.setLineDash([]);

    // Label at centroid
    const aabb = this._computeAABB(verts);
    const cx = aabb.x + aabb.w / 2;
    const cz = aabb.z + aabb.d / 2;
    const sc = this._toScreen(cx, cz);
    const sw = aabb.w * this.zoom;
    const sh = aabb.d * this.zoom;
    const labelText = item.label || item.id || '';
    const maxFontSize = Math.min(sw * 0.8 / Math.max(labelText.length * 0.55, 1), sh * 0.35, 14);
    if (maxFontSize >= 5) {
      ctx.fillStyle = '#fff';
      ctx.font = `bold ${Math.round(maxFontSize)}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(labelText, sc.x, sc.y, sw - 4);
    }
  }

  _drawHandles(item) {
    const ctx = this.ctx;
    const handles = this._getRectHandles(item);
    const hs = 5;

    handles.forEach((h, i) => {
      const s = this._toScreen(h.bx, h.bz);
      ctx.fillStyle = i === this.hoverHandle ? '#fff' : '#c9a94e';
      ctx.fillRect(s.x - hs, s.y - hs, hs * 2, hs * 2);
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 1;
      ctx.strokeRect(s.x - hs, s.y - hs, hs * 2, hs * 2);
    });
  }

  _drawVertexHandles(item) {
    const ctx = this.ctx;
    const verts = item.vertices;
    if (!verts) return;
    const radius = 6;

    verts.forEach((v, i) => {
      const s = this._toScreen(v.x, v.z);
      ctx.beginPath();
      ctx.arc(s.x, s.y, radius, 0, Math.PI * 2);
      ctx.fillStyle = i === this.hoverHandle ? '#fff' : '#c9a94e';
      ctx.fill();
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 1;
      ctx.stroke();
    });
  }

  _drawDimOverlay(item) {
    const ctx = this.ctx;
    let text;
    if (this._isPolygon(item)) {
      const area = this._polygonArea(item.vertices);
      const aabb = this._computeAABB(item.vertices);
      text = `Area: ${area.toFixed(0)} sq ft (${aabb.w.toFixed(1)}' x ${aabb.d.toFixed(1)}')`;
    } else {
      text = `${item.w.toFixed(1)}' x ${item.d.toFixed(1)}' @ (${item.x.toFixed(1)}, ${item.z.toFixed(1)})`;
    }
    const tl = this._isPolygon(item)
      ? this._toScreen(this._computeAABB(item.vertices).x, this._computeAABB(item.vertices).z)
      : this._toScreen(item.x, item.z);

    ctx.fillStyle = 'rgba(0,0,0,0.8)';
    ctx.font = 'bold 13px sans-serif';
    const tw = ctx.measureText(text).width;
    ctx.fillRect(tl.x, tl.y - 22, tw + 12, 20);
    ctx.fillStyle = '#c9a94e';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, tl.x + 6, tl.y - 12);
  }

  // ─── HANDLE GEOMETRY (Rectangles) ───

  _getRectHandles(item) {
    const { x, z, w, d } = item;
    return [
      { bx: x,         bz: z,         cursor: 'nwse-resize' },
      { bx: x + w / 2, bz: z,         cursor: 'ns-resize' },
      { bx: x + w,     bz: z,         cursor: 'nesw-resize' },
      { bx: x + w,     bz: z + d / 2, cursor: 'ew-resize' },
      { bx: x + w,     bz: z + d,     cursor: 'nwse-resize' },
      { bx: x + w / 2, bz: z + d,     cursor: 'ns-resize' },
      { bx: x,         bz: z + d,     cursor: 'nesw-resize' },
      { bx: x,         bz: z + d / 2, cursor: 'ew-resize' },
    ];
  }

  _hitTestHandle(sx, sy) {
    if (!this.selected) return -1;
    const item = this._getSelectedItem();
    if (!item) return -1;
    const threshold = 8;

    if (this._isPolygon(item)) {
      // Check vertex handles
      for (let i = 0; i < item.vertices.length; i++) {
        const s = this._toScreen(item.vertices[i].x, item.vertices[i].z);
        if (Math.abs(sx - s.x) < threshold && Math.abs(sy - s.y) < threshold) {
          return i;
        }
      }
      return -1;
    }

    const handles = this._getRectHandles(item);
    for (let i = 0; i < handles.length; i++) {
      const s = this._toScreen(handles[i].bx, handles[i].bz);
      if (Math.abs(sx - s.x) < threshold && Math.abs(sy - s.y) < threshold) {
        return i;
      }
    }
    return -1;
  }

  // ─── HIT TEST ITEMS ───

  _hitTestItem(sx, sy) {
    const bld = this._toBuilding(sx, sy);
    const items = this._getItems();

    for (let i = items.length - 1; i >= 0; i--) {
      const it = items[i];
      if (this._isPolygon(it)) {
        if (this._pointInPolygon(bld.x, bld.z, it.vertices)) {
          return { list: it._list, index: it._index };
        }
      } else {
        if (bld.x >= it.x && bld.x <= it.x + it.w &&
            bld.z >= it.z && bld.z <= it.z + it.d) {
          return { list: it._list, index: it._index };
        }
      }
    }
    return null;
  }

  // ─── SNAP ───

  _snap(val) {
    if (!this.snapEnabled) return val;
    return Math.round(val / this.snapSize) * this.snapSize;
  }

  // ─── EVENTS ───

  _bindEvents() {
    const c = this.canvas;

    this._bound.mousedown = (e) => this._onMouseDown(e);
    this._bound.mousemove = (e) => this._onMouseMove(e);
    this._bound.mouseup = (e) => this._onMouseUp(e);
    this._bound.wheel = (e) => this._onWheel(e);
    this._bound.resize = () => { this._resizeCanvas(); };
    this._bound.contextmenu = (e) => this._onContextMenu(e);
    this._bound.dblclick = (e) => this._onDoubleClick(e);

    c.addEventListener('mousedown', this._bound.mousedown);
    c.addEventListener('mousemove', this._bound.mousemove);
    c.addEventListener('mouseup', this._bound.mouseup);
    c.addEventListener('wheel', this._bound.wheel, { passive: false });
    c.addEventListener('contextmenu', this._bound.contextmenu);
    c.addEventListener('dblclick', this._bound.dblclick);
    window.addEventListener('resize', this._bound.resize);

    // Toolbar buttons
    document.querySelectorAll('.ed-tab').forEach(btn => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.ed-tab').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this.activeFloor = btn.dataset.floor;
        this.selected = null;
        this.multiSelected = [];
        this._updatePanel();
        this._updateToolbarButtons();
      });
    });

    document.getElementById('ed-snap-toggle').addEventListener('change', (e) => {
      this.snapEnabled = e.target.checked;
    });

    document.getElementById('ed-apply').addEventListener('click', () => this._applyToModel());
    document.getElementById('ed-export').addEventListener('click', () => this._exportJSON());
    document.getElementById('ed-close').addEventListener('click', () => {
      this.close();
      document.getElementById('renderCanvas').style.display = 'block';
      document.getElementById('floor-selector').style.display = '';
      document.getElementById('info-panel').style.display = '';
      document.getElementById('controls-help').style.display = '';
      document.getElementById('view-toggle').style.display = '';
      document.getElementById('compass').style.display = '';
    });

    // Add Element dropdown (also handles parking/planter/road)
    document.getElementById('ed-add-type').addEventListener('change', (e) => {
      const typeName = e.target.value;
      if (typeName === '_parking') {
        this._addParkingGroup();
      } else if (typeName === '_planter') {
        this._addPlanter();
      } else if (typeName === '_road') {
        this._addRoad();
      } else if (typeName) {
        this._addElement(typeName);
      }
      e.target.selectedIndex = 0; // reset dropdown
    });

    // Delete button (handles both interior elements and exterior elements)
    document.getElementById('ed-delete').addEventListener('click', () => {
      if (this.editingParking && this.selectedParkingIndex >= 0) {
        this._deleteExteriorSelected();
      } else if (this.editingPlanters && this.selectedPlanterIndex >= 0) {
        this._deleteExteriorSelected();
      } else if (this.editingRoads && this.selectedRoadIndex >= 0) {
        this._deleteExteriorSelected();
      } else if (this.selected) {
        this._deleteSelected();
        this._saveSnapshot();
      }
    });

    // Toggle polygon/rectangle
    document.getElementById('ed-toggle-poly').addEventListener('click', () => {
      this._togglePolygon();
      this._saveSnapshot();
    });

    // Copy / Paste buttons
    document.getElementById('ed-copy').addEventListener('click', () => this._copySelected());
    document.getElementById('ed-paste').addEventListener('click', () => this._pasteClipboard());

    // Undo / Redo buttons
    document.getElementById('ed-undo').addEventListener('click', () => this._undo());
    document.getElementById('ed-redo').addEventListener('click', () => this._redo());

    // Group / Ungroup buttons
    document.getElementById('ed-group').addEventListener('click', () => this._groupSelected());
    document.getElementById('ed-ungroup').addEventListener('click', () => this._ungroupSelected());

    // Edit Mode dropdown
    document.getElementById('ed-edit-mode').addEventListener('change', (e) => {
      const mode = e.target.value;
      if (!mode) return;
      // Activate the corresponding hidden button to trigger existing logic
      const btn = document.getElementById(`ed-${mode === 'groundPad' ? 'ground-pad' : mode}`);
      if (btn) btn.click();
      // Update dropdown label & show Done button
      this._updateEditModeDropdown();
    });

    // Done button — exit current edit mode
    document.getElementById('ed-exit-mode').addEventListener('click', () => {
      this._exitAllEditModes();
      this._updateEditModeDropdown();
      this._updateExtButtons();
      this._updatePanel();
    });

    // Edit Outline toggle
    document.getElementById('ed-outline').addEventListener('click', () => {
      this.editingOutline = !this.editingOutline;
      const btn = document.getElementById('ed-outline');
      if (this.editingOutline) {
        btn.classList.add('active');
        btn.textContent = 'Done Outline';
        this._exitAllEditModes('outline');
        this.selected = null;
        this.selectedOutlineVertex = -1;
        this._updateExtButtons();
        this._updatePanel();
        this._updateToolbarButtons();
      } else {
        btn.classList.remove('active');
        btn.textContent = 'Edit Outline';
        this.selectedOutlineVertex = -1;
        this._updatePanel();
      }
    });

    // Edit Walkway toggle
    document.getElementById('ed-walkway').addEventListener('click', () => {
      this.editingWalkway = !this.editingWalkway;
      const btn = document.getElementById('ed-walkway');
      if (this.editingWalkway) {
        btn.classList.add('active');
        btn.textContent = 'Done Walkway';
        this._exitAllEditModes('walkway');
        this.selected = null;
        this.selectedWalkwayVertex = -1;
        this._updateExtButtons();
        this._updatePanel();
        this._updateToolbarButtons();
      } else {
        btn.classList.remove('active');
        btn.textContent = 'Edit Walkway';
        this.selectedWalkwayVertex = -1;
        this._updatePanel();
      }
    });

    // Edit Stairs toggle
    document.getElementById('ed-stair').addEventListener('click', () => {
      this.editingStair = !this.editingStair;
      const btn = document.getElementById('ed-stair');
      if (this.editingStair) {
        btn.classList.add('active');
        btn.textContent = 'Done Stairs';
        this._exitAllEditModes('stair');
        this.selected = null;
        this.selectedStairVertex = -1;
        this._updateExtButtons();
        this._updatePanel();
        this._updateToolbarButtons();
      } else {
        btn.classList.remove('active');
        btn.textContent = 'Edit Stairs';
        this.selectedStairVertex = -1;
        this._updatePanel();
      }
    });

    // Edit Ground Pad toggle
    document.getElementById('ed-ground-pad').addEventListener('click', () => {
      this.editingGroundPad = !this.editingGroundPad;
      const btn = document.getElementById('ed-ground-pad');
      if (this.editingGroundPad) {
        btn.classList.add('active');
        btn.textContent = 'Done Ground';
        this._exitAllEditModes('groundPad');
        this.selected = null;
        this.selectedGroundPadVertex = -1;
        this._updateExtButtons();
        this._updatePanel();
        this._updateToolbarButtons();
      } else {
        btn.classList.remove('active');
        btn.textContent = 'Edit Ground';
        this.selectedGroundPadVertex = -1;
        this._updateExtButtons();
        this._updatePanel();
      }
    });

    // Edit Parking toggle
    document.getElementById('ed-parking').addEventListener('click', () => {
      this.editingParking = !this.editingParking;
      const btn = document.getElementById('ed-parking');
      if (this.editingParking) {
        btn.classList.add('active');
        btn.textContent = 'Done Parking';
        this._exitAllEditModes('parking');
        this.selected = null;
        this.selectedParkingIndex = -1;
        this._updateExtButtons();
        this._updatePanel();
        this._updateToolbarButtons();
      } else {
        btn.classList.remove('active');
        btn.textContent = 'Edit Parking';
        this.selectedParkingIndex = -1;
        this._updateExtButtons();
        this._updatePanel();
      }
    });

    // Edit Planters toggle
    document.getElementById('ed-planters').addEventListener('click', () => {
      this.editingPlanters = !this.editingPlanters;
      const btn = document.getElementById('ed-planters');
      if (this.editingPlanters) {
        btn.classList.add('active');
        btn.textContent = 'Done Planters';
        this._exitAllEditModes('planters');
        this.selected = null;
        this.selectedPlanterIndex = -1;
        this._updateExtButtons();
        this._updatePanel();
        this._updateToolbarButtons();
      } else {
        btn.classList.remove('active');
        btn.textContent = 'Edit Planters';
        this.selectedPlanterIndex = -1;
        this._updateExtButtons();
        this._updatePanel();
      }
    });

    // Edit Roads toggle
    document.getElementById('ed-roads').addEventListener('click', () => {
      this.editingRoads = !this.editingRoads;
      const btn = document.getElementById('ed-roads');
      if (this.editingRoads) {
        btn.classList.add('active');
        btn.textContent = 'Done Roads';
        this._exitAllEditModes('roads');
        this.selected = null;
        this.selectedRoadIndex = -1;
        this._updateExtButtons();
        this._updatePanel();
        this._updateToolbarButtons();
      } else {
        btn.classList.remove('active');
        btn.textContent = 'Edit Roads';
        this.selectedRoadIndex = -1;
        this._updateExtButtons();
        this._updatePanel();
      }
    });

    // Add Parking / Planter / Road buttons
    document.getElementById('ed-add-parking').addEventListener('click', () => this._addParkingGroup());
    document.getElementById('ed-add-planter').addEventListener('click', () => this._addPlanter());
    document.getElementById('ed-add-road').addEventListener('click', () => this._addRoad());
    document.getElementById('ed-delete-ext').addEventListener('click', () => this._deleteExteriorSelected());

    // Keyboard shortcuts
    this._bound.keydown = (e) => this._onKeyDown(e);
    window.addEventListener('keydown', this._bound.keydown);
  }

  _exitAllEditModes(except) {
    this._dismissMeasurementInput();
    this.multiSelected = [];
    if (except !== 'outline' && this.editingOutline) {
      this.editingOutline = false;
      this.selectedOutlineVertex = -1;
      const b = document.getElementById('ed-outline');
      if (b) { b.classList.remove('active'); b.textContent = 'Edit Outline'; }
    }
    if (except !== 'walkway' && this.editingWalkway) {
      this.editingWalkway = false;
      this.selectedWalkwayVertex = -1;
      const b = document.getElementById('ed-walkway');
      if (b) { b.classList.remove('active'); b.textContent = 'Edit Walkway'; }
    }
    if (except !== 'stair' && this.editingStair) {
      this.editingStair = false;
      this.selectedStairVertex = -1;
      const b = document.getElementById('ed-stair');
      if (b) { b.classList.remove('active'); b.textContent = 'Edit Stairs'; }
    }
    if (except !== 'groundPad' && this.editingGroundPad) {
      this.editingGroundPad = false;
      this.selectedGroundPadVertex = -1;
      const b = document.getElementById('ed-ground-pad');
      if (b) { b.classList.remove('active'); b.textContent = 'Edit Ground'; }
    }
    if (except !== 'parking' && this.editingParking) {
      this.editingParking = false;
      this.selectedParkingIndex = -1;
      const b = document.getElementById('ed-parking');
      if (b) { b.classList.remove('active'); b.textContent = 'Edit Parking'; }
    }
    if (except !== 'planters' && this.editingPlanters) {
      this.editingPlanters = false;
      this.selectedPlanterIndex = -1;
      const b = document.getElementById('ed-planters');
      if (b) { b.classList.remove('active'); b.textContent = 'Edit Planters'; }
    }
    if (except !== 'roads' && this.editingRoads) {
      this.editingRoads = false;
      this.selectedRoadIndex = -1;
      const b = document.getElementById('ed-roads');
      if (b) { b.classList.remove('active'); b.textContent = 'Edit Roads'; }
    }
  }

  _updateExtButtons() {
    const addPark = document.getElementById('ed-add-parking');
    const addPl = document.getElementById('ed-add-planter');
    const addRd = document.getElementById('ed-add-road');
    const delExt = document.getElementById('ed-delete-ext');
    if (addPark) addPark.style.display = this.editingParking ? '' : 'none';
    if (addPl) addPl.style.display = this.editingPlanters ? '' : 'none';
    if (addRd) addRd.style.display = this.editingRoads ? '' : 'none';
    if (delExt) delExt.style.display = (this.editingParking || this.editingPlanters || this.editingRoads) ? '' : 'none';
    this._updateEditModeDropdown();
  }

  _updateEditModeDropdown() {
    const dropdown = document.getElementById('ed-edit-mode');
    const doneBtn = document.getElementById('ed-exit-mode');
    if (!dropdown) return;
    const anyActive = this.editingOutline || this.editingWalkway || this.editingStair ||
      this.editingGroundPad || this.editingParking || this.editingPlanters || this.editingRoads;
    if (anyActive) {
      // Determine which mode is active and set the dropdown value
      let activeMode = '';
      if (this.editingOutline) activeMode = 'outline';
      else if (this.editingWalkway) activeMode = 'walkway';
      else if (this.editingStair) activeMode = 'stair';
      else if (this.editingGroundPad) activeMode = 'groundPad';
      else if (this.editingParking) activeMode = 'parking';
      else if (this.editingPlanters) activeMode = 'planters';
      else if (this.editingRoads) activeMode = 'roads';
      dropdown.value = activeMode;
      if (doneBtn) doneBtn.style.display = '';
    } else {
      dropdown.value = '';
      if (doneBtn) doneBtn.style.display = 'none';
    }
  }

  _unbindEvents() {
    const c = this.canvas;
    if (!c) return;
    c.removeEventListener('mousedown', this._bound.mousedown);
    c.removeEventListener('mousemove', this._bound.mousemove);
    c.removeEventListener('mouseup', this._bound.mouseup);
    c.removeEventListener('wheel', this._bound.wheel);
    c.removeEventListener('contextmenu', this._bound.contextmenu);
    c.removeEventListener('dblclick', this._bound.dblclick);
    window.removeEventListener('resize', this._bound.resize);
    window.removeEventListener('keydown', this._bound.keydown);
  }

  _onKeyDown(e) {
    // Only handle when editor is visible
    if (!this.container || this.container.style.display === 'none') return;
    // Don't intercept when typing in inputs
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.tagName === 'TEXTAREA') return;

    const ctrl = e.ctrlKey || e.metaKey;

    if (ctrl && e.key === 'z' && !e.shiftKey) {
      e.preventDefault();
      this._undo();
    } else if (ctrl && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
      e.preventDefault();
      this._redo();
    } else if (ctrl && e.key === 'c') {
      e.preventDefault();
      this._copySelected();
    } else if (ctrl && e.key === 'v') {
      e.preventDefault();
      this._pasteClipboard();
    } else if (ctrl && e.key === 'g' && !e.shiftKey) {
      e.preventDefault();
      this._groupSelected();
    } else if (ctrl && e.key === 'g' && e.shiftKey) {
      e.preventDefault();
      this._ungroupSelected();
    } else if (e.key === 'Delete' || e.key === 'Backspace') {
      if (this.editingParking && this.selectedParkingIndex >= 0) {
        e.preventDefault();
        this._deleteExteriorSelected();
      } else if (this.editingPlanters && this.selectedPlanterIndex >= 0) {
        e.preventDefault();
        this._deleteExteriorSelected();
      } else if (this.editingRoads && this.selectedRoadIndex >= 0) {
        e.preventDefault();
        this._deleteExteriorSelected();
      } else if (this.selected) {
        e.preventDefault();
        this._deleteSelected();
        this._saveSnapshot();
      }
    }
  }

  _onDoubleClick(e) {
    if (this.editingOutline || this.editingWalkway || this.editingStair ||
        this.editingGroundPad || this.editingParking || this.editingPlanters || this.editingRoads) return;
    const pos = this._getCanvasPos(e);
    const hit = this._hitTestItem(pos.x, pos.y);
    if (!hit) return;

    const group = this._findGroupContaining(hit);
    if (group) {
      const members = this._getGroupMembers(group);
      if (members.length > 0) {
        this.selected = members[0];
        this.multiSelected = members.slice(1);
        this._updatePanel();
        this._updateToolbarButtons();
      }
    }
  }

  _getCanvasPos(e) {
    const rect = this.canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  _onContextMenu(e) {
    e.preventDefault();

    const pos = this._getCanvasPos(e);
    const bld = this._toBuilding(pos.x, pos.y);
    const threshold = 8;

    // Outline editing context menu
    if (this.editingOutline) {
      const verts = this.outlineVertices;

      // Right-click on vertex → remove it (min 3)
      for (let i = 0; i < verts.length; i++) {
        const s = this._toScreen(verts[i].x, verts[i].z);
        if (Math.abs(pos.x - s.x) < threshold && Math.abs(pos.y - s.y) < threshold) {
          if (verts.length > 3) {
            verts.splice(i, 1);
            this.selectedOutlineVertex = -1;
            this._saveSnapshot();
            this._updateOutlinePanel();
          }
          return;
        }
      }

      // Right-click on edge → add vertex
      const edgeThreshold = 10 / this.zoom;
      for (let i = 0; i < verts.length; i++) {
        const j = (i + 1) % verts.length;
        const dist = this._pointToSegmentDist(bld.x, bld.z,
          verts[i].x, verts[i].z, verts[j].x, verts[j].z);
        if (dist < edgeThreshold) {
          const newVert = { x: this._snap(bld.x), z: this._snap(bld.z) };
          verts.splice(j, 0, newVert);
          this.selectedOutlineVertex = j;
          this._saveSnapshot();
          this._updateOutlinePanel();
          return;
        }
      }
      return;
    }

    // Walkway editing context menu
    if (this.editingWalkway) {
      const verts = this.walkwayVertices;

      // Right-click on vertex → remove it (min 3)
      for (let i = 0; i < verts.length; i++) {
        const s = this._toScreen(verts[i].x, verts[i].z);
        if (Math.abs(pos.x - s.x) < threshold && Math.abs(pos.y - s.y) < threshold) {
          if (verts.length > 3) {
            verts.splice(i, 1);
            this.selectedWalkwayVertex = -1;
            this._saveSnapshot();
            this._updateWalkwayPanel();
          }
          return;
        }
      }

      // Right-click on edge → add vertex
      const edgeThreshold = 10 / this.zoom;
      for (let i = 0; i < verts.length; i++) {
        const j = (i + 1) % verts.length;
        const dist = this._pointToSegmentDist(bld.x, bld.z,
          verts[i].x, verts[i].z, verts[j].x, verts[j].z);
        if (dist < edgeThreshold) {
          const newVert = { x: this._snap(bld.x), z: this._snap(bld.z) };
          verts.splice(j, 0, newVert);
          this.selectedWalkwayVertex = j;
          this._saveSnapshot();
          this._updateWalkwayPanel();
          return;
        }
      }
      return;
    }

    // Ground pad editing context menu
    if (this.editingGroundPad) {
      const verts = this.groundPadVertices;

      for (let i = 0; i < verts.length; i++) {
        const s = this._toScreen(verts[i].x, verts[i].z);
        if (Math.abs(pos.x - s.x) < threshold && Math.abs(pos.y - s.y) < threshold) {
          if (verts.length > 3) {
            verts.splice(i, 1);
            this.selectedGroundPadVertex = -1;
            this._saveSnapshot();
            this._updateGroundPadPanel();
          }
          return;
        }
      }

      const edgeThreshold = 10 / this.zoom;
      for (let i = 0; i < verts.length; i++) {
        const j = (i + 1) % verts.length;
        const dist = this._pointToSegmentDist(bld.x, bld.z,
          verts[i].x, verts[i].z, verts[j].x, verts[j].z);
        if (dist < edgeThreshold) {
          const newVert = { x: this._snap(bld.x), z: this._snap(bld.z) };
          verts.splice(j, 0, newVert);
          this.selectedGroundPadVertex = j;
          this._saveSnapshot();
          this._updateGroundPadPanel();
          return;
        }
      }
      return;
    }

    // Road editing context menu
    if (this.editingRoads && this.selectedRoadIndex >= 0) {
      const rd = this.roads[this.selectedRoadIndex];
      const pts = rd.points;

      // Right-click on control point → remove (min 2)
      for (let i = 0; i < pts.length; i++) {
        const s = this._toScreen(pts[i].x, pts[i].z);
        if (Math.abs(pos.x - s.x) < threshold && Math.abs(pos.y - s.y) < threshold) {
          if (pts.length > 2) {
            pts.splice(i, 1);
            this._saveSnapshot();
            this._updateRoadPanel();
          }
          return;
        }
      }

      // Right-click on centerline edge → add control point
      const edgeThreshold = 15 / this.zoom;
      for (let i = 0; i < pts.length - 1; i++) {
        const dist = this._pointToSegmentDist(bld.x, bld.z,
          pts[i].x, pts[i].z, pts[i + 1].x, pts[i + 1].z);
        if (dist < edgeThreshold) {
          const newPt = { x: this._snap(bld.x), z: this._snap(bld.z) };
          pts.splice(i + 1, 0, newPt);
          this._saveSnapshot();
          this._updateRoadPanel();
          return;
        }
      }
      return;
    }

    // Stair editing context menu
    if (this.editingStair) {
      const verts = this.stairVertices;

      for (let i = 0; i < verts.length; i++) {
        const s = this._toScreen(verts[i].x, verts[i].z);
        if (Math.abs(pos.x - s.x) < threshold && Math.abs(pos.y - s.y) < threshold) {
          if (verts.length > 3) {
            verts.splice(i, 1);
            this.selectedStairVertex = -1;
            this._saveSnapshot();
            this._updateStairPanel();
          }
          return;
        }
      }

      const edgeThreshold = 10 / this.zoom;
      for (let i = 0; i < verts.length; i++) {
        const j = (i + 1) % verts.length;
        const dist = this._pointToSegmentDist(bld.x, bld.z,
          verts[i].x, verts[i].z, verts[j].x, verts[j].z);
        if (dist < edgeThreshold) {
          const newVert = { x: this._snap(bld.x), z: this._snap(bld.z) };
          verts.splice(j, 0, newVert);
          this.selectedStairVertex = j;
          this._saveSnapshot();
          this._updateStairPanel();
          return;
        }
      }
      return;
    }

    if (!this.selected) return;
    const item = this._getSelectedItem();
    if (!item || !this._isPolygon(item)) return;

    const verts = item.vertices;

    // Check if right-clicking on a vertex → remove it
    for (let i = 0; i < verts.length; i++) {
      const s = this._toScreen(verts[i].x, verts[i].z);
      if (Math.abs(pos.x - s.x) < threshold && Math.abs(pos.y - s.y) < threshold) {
        if (verts.length > 3) {
          verts.splice(i, 1);
          const aabb = this._computeAABB(verts);
          item.x = aabb.x; item.z = aabb.z; item.w = aabb.w; item.d = aabb.d;
          this.hoverHandle = -1;
          this._saveSnapshot();
          this._updatePanel();
        }
        return;
      }
    }

    // Check if right-clicking on an edge → add vertex
    const edgeThreshold = 10 / this.zoom;
    for (let i = 0; i < verts.length; i++) {
      const j = (i + 1) % verts.length;
      const dist = this._pointToSegmentDist(bld.x, bld.z,
        verts[i].x, verts[i].z, verts[j].x, verts[j].z);
      if (dist < edgeThreshold) {
        const newVert = { x: this._snap(bld.x), z: this._snap(bld.z) };
        verts.splice(j, 0, newVert);
        const aabb = this._computeAABB(verts);
        item.x = aabb.x; item.z = aabb.z; item.w = aabb.w; item.d = aabb.d;
        this._saveSnapshot();
        this._updatePanel();
        return;
      }
    }
  }

  _onMouseDown(e) {
    const pos = this._getCanvasPos(e);

    // Right-click or middle-click → pan
    if (e.button === 1 || e.button === 2) {
      this.panning = true;
      this.panStart = pos;
      this.panOriginal = { ...this.pan };
      return;
    }

    // Left click

    // If measurement input is open, dismiss it and consume click
    if (this._measurementInput) {
      this._dismissMeasurementInput();
      return;
    }

    // ── Check measurement label click ──
    if (this.measurementHitAreas.length > 0) {
      const hit = this._hitTestMeasurement(pos.x, pos.y);
      if (hit) {
        this._showMeasurementInput(hit);
        return;
      }
    }

    // ── Outline editing mode ──
    if (this.editingOutline) {
      const threshold = 10;
      const verts = this.outlineVertices;

      // Check if clicking on an outline vertex
      for (let i = 0; i < verts.length; i++) {
        const s = this._toScreen(verts[i].x, verts[i].z);
        if (Math.abs(pos.x - s.x) < threshold && Math.abs(pos.y - s.y) < threshold) {
          this.selectedOutlineVertex = i;
          this.draggingOutlineVertex = true;
          this.dragStart = this._toBuilding(pos.x, pos.y);
          this.dragOriginal = { x: verts[i].x, z: verts[i].z };
          this._updateOutlinePanel();
          return;
        }
      }

      // Check edge hit for whole-polygon drag constrained to normal
      const bldO = this._toBuilding(pos.x, pos.y);
      const edgeThresholdO = 8 / this.zoom;
      const edgeHitO = this._hitTestEdge(verts, bldO.x, bldO.z, edgeThresholdO);
      if (edgeHitO) {
        this.draggingEdge = true;
        this.edgeDragTarget = 'outline';
        this.edgeDragNormal = { nx: edgeHitO.nx, nz: edgeHitO.nz };
        this.edgeDragOriginalVerts = verts.map(v => ({ ...v }));
        this.dragStart = bldO;
        return;
      }

      // Click on empty → deselect vertex or pan
      this.selectedOutlineVertex = -1;
      this._updateOutlinePanel();
      this.panning = true;
      this.panStart = pos;
      this.panOriginal = { ...this.pan };
      return;
    }

    // ── Walkway editing mode ──
    if (this.editingWalkway) {
      const threshold = 10;
      const verts = this.walkwayVertices;

      // Check if clicking on a walkway vertex
      for (let i = 0; i < verts.length; i++) {
        const s = this._toScreen(verts[i].x, verts[i].z);
        if (Math.abs(pos.x - s.x) < threshold && Math.abs(pos.y - s.y) < threshold) {
          this.selectedWalkwayVertex = i;
          this.draggingWalkwayVertex = true;
          this.dragStart = this._toBuilding(pos.x, pos.y);
          this.dragOriginal = { x: verts[i].x, z: verts[i].z };
          this._updateWalkwayPanel();
          return;
        }
      }

      // Check edge hit for whole-polygon drag constrained to normal
      const bldW = this._toBuilding(pos.x, pos.y);
      const edgeThresholdW = 8 / this.zoom;
      const edgeHitW = this._hitTestEdge(verts, bldW.x, bldW.z, edgeThresholdW);
      if (edgeHitW) {
        this.draggingEdge = true;
        this.edgeDragTarget = 'walkway';
        this.edgeDragNormal = { nx: edgeHitW.nx, nz: edgeHitW.nz };
        this.edgeDragOriginalVerts = verts.map(v => ({ ...v }));
        this.dragStart = bldW;
        return;
      }

      // Click on empty → deselect vertex or pan
      this.selectedWalkwayVertex = -1;
      this._updateWalkwayPanel();
      this.panning = true;
      this.panStart = pos;
      this.panOriginal = { ...this.pan };
      return;
    }

    // ── Stair editing mode ──
    if (this.editingStair) {
      const threshold = 10;
      const verts = this.stairVertices;

      for (let i = 0; i < verts.length; i++) {
        const s = this._toScreen(verts[i].x, verts[i].z);
        if (Math.abs(pos.x - s.x) < threshold && Math.abs(pos.y - s.y) < threshold) {
          this.selectedStairVertex = i;
          this.draggingStairVertex = true;
          this.dragStart = this._toBuilding(pos.x, pos.y);
          this.dragOriginal = { x: verts[i].x, z: verts[i].z };
          this._updateStairPanel();
          return;
        }
      }

      // Check edge hit for whole-polygon drag constrained to normal
      const bldSt = this._toBuilding(pos.x, pos.y);
      const edgeThresholdSt = 8 / this.zoom;
      const edgeHitSt = this._hitTestEdge(verts, bldSt.x, bldSt.z, edgeThresholdSt);
      if (edgeHitSt) {
        this.draggingEdge = true;
        this.edgeDragTarget = 'stair';
        this.edgeDragNormal = { nx: edgeHitSt.nx, nz: edgeHitSt.nz };
        this.edgeDragOriginalVerts = verts.map(v => ({ ...v }));
        this.dragStart = bldSt;
        return;
      }

      this.selectedStairVertex = -1;
      this._updateStairPanel();
      this.panning = true;
      this.panStart = pos;
      this.panOriginal = { ...this.pan };
      return;
    }

    // ── Ground Pad editing mode ──
    if (this.editingGroundPad) {
      const threshold = 10;
      const verts = this.groundPadVertices;

      for (let i = 0; i < verts.length; i++) {
        const s = this._toScreen(verts[i].x, verts[i].z);
        if (Math.abs(pos.x - s.x) < threshold && Math.abs(pos.y - s.y) < threshold) {
          this.selectedGroundPadVertex = i;
          this.draggingGroundPadVertex = true;
          this.dragStart = this._toBuilding(pos.x, pos.y);
          this.dragOriginal = { x: verts[i].x, z: verts[i].z };
          this._updateGroundPadPanel();
          return;
        }
      }

      // Check edge hit for whole-polygon drag constrained to normal
      const bldGP = this._toBuilding(pos.x, pos.y);
      const edgeThresholdGP = 8 / this.zoom;
      const edgeHitGP = this._hitTestEdge(verts, bldGP.x, bldGP.z, edgeThresholdGP);
      if (edgeHitGP) {
        this.draggingEdge = true;
        this.edgeDragTarget = 'groundPad';
        this.edgeDragNormal = { nx: edgeHitGP.nx, nz: edgeHitGP.nz };
        this.edgeDragOriginalVerts = verts.map(v => ({ ...v }));
        this.dragStart = bldGP;
        return;
      }

      this.selectedGroundPadVertex = -1;
      this._updateGroundPadPanel();
      this.panning = true;
      this.panStart = pos;
      this.panOriginal = { ...this.pan };
      return;
    }

    // ── Parking editing mode ──
    if (this.editingParking) {
      const bld = this._toBuilding(pos.x, pos.y);
      const threshold = 10;

      // Check vertex handles of selected group first
      if (this.selectedParkingIndex >= 0) {
        const grp = this.parkingGroups[this.selectedParkingIndex];
        if (grp && grp.vertices) {
          for (let vi = 0; vi < grp.vertices.length; vi++) {
            const s = this._toScreen(grp.vertices[vi].x, grp.vertices[vi].z);
            if (Math.abs(pos.x - s.x) < threshold && Math.abs(pos.y - s.y) < threshold) {
              this.draggingParkingVertex = vi;
              this.dragStart = this._toBuilding(pos.x, pos.y);
              this.parkingDragOriginal = { x: grp.vertices[vi].x, z: grp.vertices[vi].z };
              return;
            }
          }
        }
      }

      // Check if clicking inside a parking group polygon
      for (let gi = this.parkingGroups.length - 1; gi >= 0; gi--) {
        const grp = this.parkingGroups[gi];
        if (grp.vertices && this._pointInPolygon(bld.x, bld.z, grp.vertices)) {
          this.selectedParkingIndex = gi;
          this.draggingParking = true;
          this.dragStart = bld;
          this.parkingDragOriginal = grp.vertices.map(v => ({ ...v }));
          this._updateParkingPanel();
          return;
        }
      }

      this.selectedParkingIndex = -1;
      this._updateParkingPanel();
      this.panning = true;
      this.panStart = pos;
      this.panOriginal = { ...this.pan };
      return;
    }

    // ── Roads editing mode ──
    if (this.editingRoads) {
      const bld = this._toBuilding(pos.x, pos.y);
      const threshold = 10;

      // Check control point handles of selected road first
      if (this.selectedRoadIndex >= 0) {
        const rd = this.roads[this.selectedRoadIndex];
        if (rd && rd.points) {
          for (let pi = 0; pi < rd.points.length; pi++) {
            const s = this._toScreen(rd.points[pi].x, rd.points[pi].z);
            if (Math.abs(pos.x - s.x) < threshold && Math.abs(pos.y - s.y) < threshold) {
              this.draggingRoadPoint = pi;
              this.dragStart = bld;
              this.dragOriginal = { x: rd.points[pi].x, z: rd.points[pi].z };
              return;
            }
          }
        }
      }

      // Check if clicking inside a road polygon
      const hitIdx = this._hitTestRoad(bld.x, bld.z);
      if (hitIdx >= 0) {
        this.selectedRoadIndex = hitIdx;
        this.draggingRoad = true;
        this.dragStart = bld;
        const rd = this.roads[hitIdx];
        this.dragOriginal = { points: rd.points.map(p => ({ ...p })) };
        this._updateRoadPanel();
        return;
      }

      this.selectedRoadIndex = -1;
      this._updateRoadPanel();
      this.panning = true;
      this.panStart = pos;
      this.panOriginal = { ...this.pan };
      return;
    }

    // ── Planters editing mode ──
    if (this.editingPlanters) {
      const bld = this._toBuilding(pos.x, pos.y);
      const threshold = 8;

      // Check resize handle of selected planter
      if (this.selectedPlanterIndex >= 0) {
        const pl = this.planters[this.selectedPlanterIndex];
        const sc = this._toScreen(pl.x, pl.z);
        const isRect = (pl.shape || 'round') === 'rect';
        if (isRect) {
          // Corner handle in local space
          const w = (pl.width || pl.diameter || 5) * this.zoom;
          const d = (pl.depth || pl.diameter || 5) * this.zoom;
          const ang = -(pl.angle || 0) * Math.PI / 180;
          const cosA = Math.cos(ang), sinA = Math.sin(ang);
          const hxLocal = w / 2, hyLocal = d / 2;
          const hx = sc.x + hxLocal * cosA - hyLocal * sinA;
          const hy = sc.y + hxLocal * sinA + hyLocal * cosA;
          if (Math.abs(pos.x - hx) < threshold && Math.abs(pos.y - hy) < threshold) {
            this.resizingPlanter = true;
            this.dragStart = bld;
            return;
          }
        } else {
          const r = (pl.diameter || 5) / 2 * this.zoom;
          const hx = sc.x + r;
          const hy = sc.y;
          if (Math.abs(pos.x - hx) < threshold && Math.abs(pos.y - hy) < threshold) {
            this.resizingPlanter = true;
            this.dragStart = bld;
            return;
          }
        }
      }

      // Check if clicking inside a planter (circle or rotated rect)
      for (let pi = this.planters.length - 1; pi >= 0; pi--) {
        const pl = this.planters[pi];
        const hitPad = 10 / this.zoom;
        if ((pl.shape || 'round') === 'rect') {
          const ang = -(pl.angle || 0) * Math.PI / 180;
          const dx = bld.x - pl.x, dz = bld.z - pl.z;
          const lx = dx * Math.cos(ang) - dz * Math.sin(ang);
          const lz = dx * Math.sin(ang) + dz * Math.cos(ang);
          const hw = (pl.width || 5) / 2 + hitPad;
          const hd = (pl.depth || 5) / 2 + hitPad;
          if (Math.abs(lx) <= hw && Math.abs(lz) <= hd) {
            this.selectedPlanterIndex = pi;
            this.draggingPlanter = true;
            this.dragStart = bld;
            this.dragOriginal = { x: pl.x, z: pl.z };
            this._updatePlanterPanel();
            return;
          }
        } else {
          const dist = Math.hypot(bld.x - pl.x, bld.z - pl.z);
          const hitR = Math.max((pl.diameter || 5) / 2, hitPad);
          if (dist <= hitR) {
            this.selectedPlanterIndex = pi;
            this.draggingPlanter = true;
            this.dragStart = bld;
            this.dragOriginal = { x: pl.x, z: pl.z };
            this._updatePlanterPanel();
            return;
          }
        }
      }

      this.selectedPlanterIndex = -1;
      this._updatePlanterPanel();
      this.panning = true;
      this.panStart = pos;
      this.panOriginal = { ...this.pan };
      return;
    }

    // ── Normal element editing mode ──

    // Check handle first (only when single-selected, not multi-selected)
    const handleIdx = this._hitTestHandle(pos.x, pos.y);
    if (handleIdx >= 0 && this.multiSelected.length === 0) {
      const sel = this._getSelectedItem();
      if (this._isPolygon(sel)) {
        // Vertex drag
        this.resizing = true;
        this.hoverHandle = handleIdx;
        this.dragStart = this._toBuilding(pos.x, pos.y);
        this.dragOriginal = { x: sel.vertices[handleIdx].x, z: sel.vertices[handleIdx].z };
      } else {
        this.resizing = true;
        this.hoverHandle = handleIdx;
        this.dragStart = this._toBuilding(pos.x, pos.y);
        this.dragOriginal = { x: sel.x, z: sel.z, w: sel.w, d: sel.d };
      }
      return;
    }

    // ── Shift+drag on empty → marquee area select ──
    if (e.shiftKey) {
      const hitShift = this._hitTestItem(pos.x, pos.y);
      if (!hitShift) {
        const bldM = this._toBuilding(pos.x, pos.y);
        this.marquee = { startX: bldM.x, startZ: bldM.z, endX: bldM.x, endZ: bldM.z };
        this.marqueeScreen = { x1: pos.x, y1: pos.y, x2: pos.x, y2: pos.y };
        return;
      }
    }

    // Check item
    const hit = this._hitTestItem(pos.x, pos.y);
    if (hit) {
      if (e.ctrlKey || e.metaKey) {
        // Ctrl+click: toggle in/out of multi-select
        const allSel = this._getAllSelected();
        const existing = allSel.findIndex(s => s.list === hit.list && s.index === hit.index);
        if (existing >= 0) {
          // Remove from selection
          if (this.selected && this.selected.list === hit.list && this.selected.index === hit.index) {
            this.selected = this.multiSelected.shift() || null;
          } else {
            this.multiSelected = this.multiSelected.filter(s => !(s.list === hit.list && s.index === hit.index));
          }
        } else {
          // Add to selection
          if (!this.selected) {
            this.selected = hit;
          } else {
            this.multiSelected.push(hit);
          }
        }
        this._updatePanel();
        this._updateToolbarButtons();
        return; // Don't start drag on ctrl+click
      }

      // Normal click — clear multi-select, select single item
      this.multiSelected = [];
      this.selected = hit;
      this._updatePanel();
      this._updateToolbarButtons();

      // If item belongs to a group, auto-include all group members for drag
      const group = this._findGroupContaining(this.selected);
      if (group) {
        const members = this._getGroupMembers(group);
        if (members.length > 1) {
          this.selected = members[0];
          this.multiSelected = members.slice(1);
        }
      }

      // Start drag — store originals for all selected items
      this.dragging = true;
      this.dragStart = this._toBuilding(pos.x, pos.y);
      this.dragOriginal = {
        items: this._getAllSelected().map(sel => {
          const item = this._getItemRef(sel.list, sel.index);
          return this._isPolygon(item)
            ? { sel, vertices: item.vertices.map(v => ({ ...v })) }
            : { sel, x: item.x, z: item.z };
        })
      };
      return;
    }

    // Click on empty → deselect or start panning
    this.selected = null;
    this.multiSelected = [];
    this._updatePanel();
    this._updateToolbarButtons();
    this.panning = true;
    this.panStart = pos;
    this.panOriginal = { ...this.pan };
  }

  _onMouseMove(e) {
    const pos = this._getCanvasPos(e);

    if (this.panning) {
      const dx = (pos.x - this.panStart.x) / this.zoom;
      const dy = (pos.y - this.panStart.y) / this.zoom;
      this.pan.x = this.panOriginal.x - dx;
      this.pan.y = this.panOriginal.y - dy;
      return;
    }

    // Edge-constrained polygon drag
    if (this.draggingEdge && this.edgeDragTarget) {
      const bld = this._toBuilding(pos.x, pos.y);
      const dx = bld.x - this.dragStart.x;
      const dz = bld.z - this.dragStart.z;
      const { nx, nz } = this.edgeDragNormal;
      const proj = dx * nx + dz * nz;
      const mx = this._snap(proj * nx);
      const mz = this._snap(proj * nz);

      const verts = this.edgeDragTarget === 'outline' ? this.outlineVertices :
                    this.edgeDragTarget === 'walkway' ? this.walkwayVertices :
                    this.edgeDragTarget === 'stair' ? this.stairVertices : this.groundPadVertices;
      const orig = this.edgeDragOriginalVerts;
      for (let i = 0; i < verts.length; i++) {
        verts[i].x = orig[i].x + mx;
        verts[i].z = orig[i].z + mz;
      }
      // Update panel values for the active mode
      if (this.edgeDragTarget === 'outline') this._updateOutlinePanelValues();
      else if (this.edgeDragTarget === 'walkway') this._updateWalkwayPanelValues();
      else if (this.edgeDragTarget === 'stair') this._updateStairPanelValues();
      else if (this.edgeDragTarget === 'groundPad') this._updateGroundPadPanelValues();
      return;
    }

    // Marquee drag
    if (this.marquee) {
      const bld = this._toBuilding(pos.x, pos.y);
      this.marquee.endX = bld.x;
      this.marquee.endZ = bld.z;
      this.marqueeScreen.x2 = pos.x;
      this.marqueeScreen.y2 = pos.y;
      return;
    }

    // Outline vertex dragging
    if (this.draggingOutlineVertex && this.selectedOutlineVertex >= 0) {
      const bld = this._toBuilding(pos.x, pos.y);
      const v = this.outlineVertices[this.selectedOutlineVertex];
      v.x = this._snap(bld.x);
      v.z = this._snap(bld.z);
      this._updateOutlinePanelValues();
      return;
    }

    // Outline hover cursor
    if (this.editingOutline) {
      const threshold = 10;
      const verts = this.outlineVertices;
      for (let i = 0; i < verts.length; i++) {
        const s = this._toScreen(verts[i].x, verts[i].z);
        if (Math.abs(pos.x - s.x) < threshold && Math.abs(pos.y - s.y) < threshold) {
          this.canvas.style.cursor = 'crosshair';
          return;
        }
      }
      const bldOH = this._toBuilding(pos.x, pos.y);
      const edgeHitOH = this._hitTestEdge(verts, bldOH.x, bldOH.z, 8 / this.zoom);
      if (edgeHitOH) { this.canvas.style.cursor = 'move'; return; }
      this.canvas.style.cursor = 'default';
      return;
    }

    // Walkway vertex dragging
    if (this.draggingWalkwayVertex && this.selectedWalkwayVertex >= 0) {
      const bld = this._toBuilding(pos.x, pos.y);
      const v = this.walkwayVertices[this.selectedWalkwayVertex];
      v.x = this._snap(bld.x);
      v.z = this._snap(bld.z);
      this._updateWalkwayPanelValues();
      return;
    }

    // Walkway hover cursor
    if (this.editingWalkway) {
      const threshold = 10;
      const verts = this.walkwayVertices;
      for (let i = 0; i < verts.length; i++) {
        const s = this._toScreen(verts[i].x, verts[i].z);
        if (Math.abs(pos.x - s.x) < threshold && Math.abs(pos.y - s.y) < threshold) {
          this.canvas.style.cursor = 'crosshair';
          return;
        }
      }
      const bldWH = this._toBuilding(pos.x, pos.y);
      const edgeHitWH = this._hitTestEdge(verts, bldWH.x, bldWH.z, 8 / this.zoom);
      if (edgeHitWH) { this.canvas.style.cursor = 'move'; return; }
      this.canvas.style.cursor = 'default';
      return;
    }

    // Stair vertex dragging
    if (this.draggingStairVertex && this.selectedStairVertex >= 0) {
      const bld = this._toBuilding(pos.x, pos.y);
      const v = this.stairVertices[this.selectedStairVertex];
      v.x = this._snap(bld.x);
      v.z = this._snap(bld.z);
      this._updateStairPanelValues();
      return;
    }

    // Stair hover cursor
    if (this.editingStair) {
      const threshold = 10;
      const verts = this.stairVertices;
      for (let i = 0; i < verts.length; i++) {
        const s = this._toScreen(verts[i].x, verts[i].z);
        if (Math.abs(pos.x - s.x) < threshold && Math.abs(pos.y - s.y) < threshold) {
          this.canvas.style.cursor = 'crosshair';
          return;
        }
      }
      const bldStH = this._toBuilding(pos.x, pos.y);
      const edgeHitStH = this._hitTestEdge(verts, bldStH.x, bldStH.z, 8 / this.zoom);
      if (edgeHitStH) { this.canvas.style.cursor = 'move'; return; }
      this.canvas.style.cursor = 'default';
      return;
    }

    // Ground pad vertex dragging
    if (this.draggingGroundPadVertex && this.selectedGroundPadVertex >= 0) {
      const bld = this._toBuilding(pos.x, pos.y);
      const v = this.groundPadVertices[this.selectedGroundPadVertex];
      v.x = this._snap(bld.x);
      v.z = this._snap(bld.z);
      this._updateGroundPadPanelValues();
      return;
    }

    // Ground pad hover cursor
    if (this.editingGroundPad) {
      const threshold = 10;
      const verts = this.groundPadVertices;
      for (let i = 0; i < verts.length; i++) {
        const s = this._toScreen(verts[i].x, verts[i].z);
        if (Math.abs(pos.x - s.x) < threshold && Math.abs(pos.y - s.y) < threshold) {
          this.canvas.style.cursor = 'crosshair';
          return;
        }
      }
      const bldGPH = this._toBuilding(pos.x, pos.y);
      const edgeHitGPH = this._hitTestEdge(verts, bldGPH.x, bldGPH.z, 8 / this.zoom);
      if (edgeHitGPH) { this.canvas.style.cursor = 'move'; return; }
      this.canvas.style.cursor = 'default';
      return;
    }

    // Parking vertex dragging
    if (this.draggingParkingVertex >= 0 && this.selectedParkingIndex >= 0) {
      const bld = this._toBuilding(pos.x, pos.y);
      const grp = this.parkingGroups[this.selectedParkingIndex];
      grp.vertices[this.draggingParkingVertex].x = this._snap(bld.x);
      grp.vertices[this.draggingParkingVertex].z = this._snap(bld.z);
      this._updateParkingPanelValues();
      return;
    }

    // Parking whole-group drag
    if (this.draggingParking && this.selectedParkingIndex >= 0) {
      const bld = this._toBuilding(pos.x, pos.y);
      const dx = bld.x - this.dragStart.x;
      const dz = bld.z - this.dragStart.z;
      const grp = this.parkingGroups[this.selectedParkingIndex];
      const orig = this.parkingDragOriginal;
      // Compute center of original group
      const cx = orig.reduce((s, v) => s + v.x, 0) / orig.length + dx;
      const cz = orig.reduce((s, v) => s + v.z, 0) / orig.length + dz;
      this.alignGuides = this._computeAlignGuides(this._snap(cx), this._snap(cz), 'parking', this.selectedParkingIndex);
      for (let i = 0; i < grp.vertices.length; i++) {
        grp.vertices[i].x = this._snap(orig[i].x + dx);
        grp.vertices[i].z = this._snap(orig[i].z + dz);
      }
      this._updateParkingPanelValues();
      return;
    }

    // Parking hover cursor
    if (this.editingParking) {
      const bld = this._toBuilding(pos.x, pos.y);
      const threshold = 10;
      // Check vertex handles of selected
      if (this.selectedParkingIndex >= 0) {
        const grp = this.parkingGroups[this.selectedParkingIndex];
        if (grp && grp.vertices) {
          for (let vi = 0; vi < grp.vertices.length; vi++) {
            const s = this._toScreen(grp.vertices[vi].x, grp.vertices[vi].z);
            if (Math.abs(pos.x - s.x) < threshold && Math.abs(pos.y - s.y) < threshold) {
              this.canvas.style.cursor = 'crosshair';
              return;
            }
          }
        }
      }
      // Check inside any polygon
      for (let gi = this.parkingGroups.length - 1; gi >= 0; gi--) {
        const grp = this.parkingGroups[gi];
        if (grp.vertices && this._pointInPolygon(bld.x, bld.z, grp.vertices)) {
          this.canvas.style.cursor = 'move';
          return;
        }
      }
      this.canvas.style.cursor = 'default';
      return;
    }

    // Road point dragging
    if (this.draggingRoadPoint >= 0 && this.selectedRoadIndex >= 0) {
      const bld = this._toBuilding(pos.x, pos.y);
      const rd = this.roads[this.selectedRoadIndex];
      let newX = this._snap(bld.x);
      let newZ = this._snap(bld.z);
      this.alignGuides = this._computeAlignGuides(newX, newZ, 'road', this.selectedRoadIndex);
      rd.points[this.draggingRoadPoint].x = newX;
      rd.points[this.draggingRoadPoint].z = newZ;
      this._updateRoadPanelValues();
      return;
    }

    // Road whole drag
    if (this.draggingRoad && this.selectedRoadIndex >= 0) {
      const bld = this._toBuilding(pos.x, pos.y);
      const dx = bld.x - this.dragStart.x;
      const dz = bld.z - this.dragStart.z;
      const rd = this.roads[this.selectedRoadIndex];
      const orig = this.dragOriginal.points;
      for (let i = 0; i < rd.points.length; i++) {
        rd.points[i].x = this._snap(orig[i].x + dx);
        rd.points[i].z = this._snap(orig[i].z + dz);
      }
      this._updateRoadPanelValues();
      return;
    }

    // Road hover cursor
    if (this.editingRoads) {
      const bld = this._toBuilding(pos.x, pos.y);
      const threshold = 10;
      if (this.selectedRoadIndex >= 0) {
        const rd = this.roads[this.selectedRoadIndex];
        if (rd && rd.points) {
          for (let pi = 0; pi < rd.points.length; pi++) {
            const s = this._toScreen(rd.points[pi].x, rd.points[pi].z);
            if (Math.abs(pos.x - s.x) < threshold && Math.abs(pos.y - s.y) < threshold) {
              this.canvas.style.cursor = 'crosshair';
              return;
            }
          }
        }
      }
      const hitIdx = this._hitTestRoad(bld.x, bld.z);
      this.canvas.style.cursor = hitIdx >= 0 ? 'move' : 'default';
      return;
    }

    // Planter resize
    if (this.resizingPlanter && this.selectedPlanterIndex >= 0) {
      const bld = this._toBuilding(pos.x, pos.y);
      const pl = this.planters[this.selectedPlanterIndex];
      if ((pl.shape || 'round') === 'rect') {
        const ang = -(pl.angle || 0) * Math.PI / 180;
        const dx = bld.x - pl.x, dz = bld.z - pl.z;
        const lx = dx * Math.cos(ang) - dz * Math.sin(ang);
        const lz = dx * Math.sin(ang) + dz * Math.cos(ang);
        pl.width = Math.max(2, this._snap(Math.abs(lx) * 2));
        pl.depth = Math.max(2, this._snap(Math.abs(lz) * 2));
      } else {
        const dist = Math.hypot(bld.x - pl.x, bld.z - pl.z);
        pl.diameter = Math.max(2, this._snap(dist * 2));
      }
      this._updatePlanterPanelValues();
      return;
    }

    // Planter drag
    if (this.draggingPlanter && this.selectedPlanterIndex >= 0) {
      const bld = this._toBuilding(pos.x, pos.y);
      const dx = bld.x - this.dragStart.x;
      const dz = bld.z - this.dragStart.z;
      const pl = this.planters[this.selectedPlanterIndex];
      let newX = this._snap(this.dragOriginal.x + dx);
      let newZ = this._snap(this.dragOriginal.z + dz);
      this.alignGuides = this._computeAlignGuides(newX, newZ, 'planter', this.selectedPlanterIndex);
      pl.x = newX;
      pl.z = newZ;
      this._updatePlanterPanelValues();
      return;
    }

    // Planter hover cursor
    if (this.editingPlanters) {
      const bld = this._toBuilding(pos.x, pos.y);
      const threshold = 8;
      const hitPad = 10 / this.zoom;
      // Check resize handle of selected
      if (this.selectedPlanterIndex >= 0) {
        const pl = this.planters[this.selectedPlanterIndex];
        const sc = this._toScreen(pl.x, pl.z);
        if ((pl.shape || 'round') === 'rect') {
          const w = (pl.width || pl.diameter || 5) * this.zoom;
          const d = (pl.depth || pl.diameter || 5) * this.zoom;
          const ang = -(pl.angle || 0) * Math.PI / 180;
          const cosA = Math.cos(ang), sinA = Math.sin(ang);
          const hx = sc.x + (w / 2) * cosA - (d / 2) * sinA;
          const hy = sc.y + (w / 2) * sinA + (d / 2) * cosA;
          if (Math.abs(pos.x - hx) < threshold && Math.abs(pos.y - hy) < threshold) {
            this.canvas.style.cursor = 'nwse-resize';
            return;
          }
        } else {
          const r = (pl.diameter || 5) / 2 * this.zoom;
          if (Math.abs(pos.x - (sc.x + r)) < threshold && Math.abs(pos.y - sc.y) < threshold) {
            this.canvas.style.cursor = 'ew-resize';
            return;
          }
        }
      }
      for (let pi = this.planters.length - 1; pi >= 0; pi--) {
        const pl = this.planters[pi];
        if ((pl.shape || 'round') === 'rect') {
          const ang = -(pl.angle || 0) * Math.PI / 180;
          const dx = bld.x - pl.x, dz = bld.z - pl.z;
          const lx = dx * Math.cos(ang) - dz * Math.sin(ang);
          const lz = dx * Math.sin(ang) + dz * Math.cos(ang);
          const hw = (pl.width || 5) / 2 + hitPad;
          const hd = (pl.depth || 5) / 2 + hitPad;
          if (Math.abs(lx) <= hw && Math.abs(lz) <= hd) {
            this.canvas.style.cursor = 'move';
            return;
          }
        } else {
          const dist = Math.hypot(bld.x - pl.x, bld.z - pl.z);
          const hitR = Math.max((pl.diameter || 5) / 2, hitPad);
          if (dist <= hitR) {
            this.canvas.style.cursor = 'move';
            return;
          }
        }
      }
      this.canvas.style.cursor = 'default';
      return;
    }

    if (this.dragging && this.selected) {
      const bld = this._toBuilding(pos.x, pos.y);
      const dx = bld.x - this.dragStart.x;
      const dz = bld.z - this.dragStart.z;

      if (this.dragOriginal && this.dragOriginal.items) {
        // Multi-item drag (also handles single item with group members)
        for (const orig of this.dragOriginal.items) {
          const item = this._getItemRef(orig.sel.list, orig.sel.index);
          if (!item) continue;
          if (orig.vertices) {
            for (let i = 0; i < item.vertices.length; i++) {
              item.vertices[i].x = this._snap(orig.vertices[i].x + dx);
              item.vertices[i].z = this._snap(orig.vertices[i].z + dz);
            }
            const aabb = this._computeAABB(item.vertices);
            item.x = aabb.x; item.z = aabb.z; item.w = aabb.w; item.d = aabb.d;
          } else {
            item.x = this._snap(orig.x + dx);
            item.z = this._snap(orig.z + dz);
          }
        }
      } else {
        // Legacy single-item drag
        const sel = this._getSelectedItem();
        if (this._isPolygon(sel)) {
          const origVerts = this.dragOriginal.vertices;
          for (let i = 0; i < sel.vertices.length; i++) {
            sel.vertices[i].x = this._snap(origVerts[i].x + dx);
            sel.vertices[i].z = this._snap(origVerts[i].z + dz);
          }
          const aabb = this._computeAABB(sel.vertices);
          sel.x = aabb.x; sel.z = aabb.z; sel.w = aabb.w; sel.d = aabb.d;
        } else {
          sel.x = this._snap(this.dragOriginal.x + dx);
          sel.z = this._snap(this.dragOriginal.z + dz);
        }
      }
      this._updatePanelValues();
      return;
    }

    if (this.resizing && this.selected) {
      const bld = this._toBuilding(pos.x, pos.y);
      const sel = this._getSelectedItem();

      if (this._isPolygon(sel)) {
        // Drag single vertex
        const vi = this.hoverHandle;
        sel.vertices[vi].x = this._snap(bld.x);
        sel.vertices[vi].z = this._snap(bld.z);
        const aabb = this._computeAABB(sel.vertices);
        sel.x = aabb.x; sel.z = aabb.z; sel.w = aabb.w; sel.d = aabb.d;
      } else {
        const orig = this.dragOriginal;
        const hi = this.hoverHandle;
        let nx = orig.x, nz = orig.z, nw = orig.w, nd = orig.d;

        if (hi === 0 || hi === 7 || hi === 6) {
          const newX = this._snap(bld.x);
          nw = orig.x + orig.w - newX;
          nx = newX;
        }
        if (hi === 2 || hi === 3 || hi === 4) {
          nw = this._snap(bld.x) - orig.x;
        }
        if (hi === 0 || hi === 1 || hi === 2) {
          const newZ = this._snap(bld.z);
          nd = orig.z + orig.d - newZ;
          nz = newZ;
        }
        if (hi === 4 || hi === 5 || hi === 6) {
          nd = this._snap(bld.z) - orig.z;
        }

        if (nw >= 2) { sel.x = nx; sel.w = nw; }
        if (nd >= 2) { sel.z = nz; sel.d = nd; }
      }

      this._updatePanelValues();
      return;
    }

    // Hover: update cursor for handles
    if (this.selected) {
      const item = this._getSelectedItem();
      const handleIdx = this._hitTestHandle(pos.x, pos.y);
      this.hoverHandle = handleIdx;
      if (handleIdx >= 0) {
        if (this._isPolygon(item)) {
          this.canvas.style.cursor = 'crosshair';
        } else {
          const handles = this._getRectHandles(item);
          this.canvas.style.cursor = handles[handleIdx].cursor;
        }
        return;
      }
    }

    const hit = this._hitTestItem(pos.x, pos.y);
    this.canvas.style.cursor = hit ? 'move' : 'default';
  }

  _onMouseUp() {
    // ── Marquee selection end ──
    if (this.marquee) {
      const minX = Math.min(this.marquee.startX, this.marquee.endX);
      const maxX = Math.max(this.marquee.startX, this.marquee.endX);
      const minZ = Math.min(this.marquee.startZ, this.marquee.endZ);
      const maxZ = Math.max(this.marquee.startZ, this.marquee.endZ);

      // Only select if marquee has some minimum size
      if (maxX - minX > 1 || maxZ - minZ > 1) {
        const items = this._getItems();
        const hits = [];
        for (const it of items) {
          let itemMinX, itemMaxX, itemMinZ, itemMaxZ;
          if (this._isPolygon(it)) {
            const aabb = this._computeAABB(it.vertices);
            itemMinX = aabb.x; itemMaxX = aabb.x + aabb.w;
            itemMinZ = aabb.z; itemMaxZ = aabb.z + aabb.d;
          } else {
            itemMinX = it.x; itemMaxX = it.x + it.w;
            itemMinZ = it.z; itemMaxZ = it.z + it.d;
          }
          if (itemMaxX >= minX && itemMinX <= maxX && itemMaxZ >= minZ && itemMinZ <= maxZ) {
            hits.push({ list: it._list, index: it._index });
          }
        }

        if (hits.length > 0) {
          this.selected = hits[0];
          this.multiSelected = hits.slice(1);
        }
      }
      this.marquee = null;
      this.marqueeScreen = null;
      this._updatePanel();
      this._updateToolbarButtons();
      return;
    }

    const wasEdgeDrag = this.draggingEdge;
    const wasMutating = this.dragging || this.resizing || wasEdgeDrag ||
      this.draggingOutlineVertex || this.draggingWalkwayVertex || this.draggingStairVertex ||
      this.draggingGroundPadVertex || this.draggingParking || this.draggingParkingVertex >= 0 ||
      this.draggingPlanter || this.resizingPlanter ||
      this.draggingRoad || this.draggingRoadPoint >= 0;

    // ── Edge drag end ──
    if (wasEdgeDrag) {
      this.draggingEdge = false;
      this.edgeDragTarget = null;
      this.edgeDragNormal = null;
      this.edgeDragOriginalVerts = null;
    }
    if (this.draggingOutlineVertex) {
      this.draggingOutlineVertex = false;
      this._updateOutlinePanel();
    }
    if (this.draggingWalkwayVertex) {
      this.draggingWalkwayVertex = false;
      this._updateWalkwayPanel();
    }
    if (this.draggingStairVertex) {
      this.draggingStairVertex = false;
      this._updateStairPanel();
    }
    if (this.draggingGroundPadVertex) {
      this.draggingGroundPadVertex = false;
      this._updateGroundPadPanel();
    }
    if (this.draggingParking || this.draggingParkingVertex >= 0) {
      this.draggingParking = false;
      this.draggingParkingVertex = -1;
      this.parkingDragOriginal = null;
      this._updateParkingPanel();
    }
    if (this.draggingPlanter || this.resizingPlanter) {
      this.draggingPlanter = false;
      this.resizingPlanter = false;
      this._updatePlanterPanel();
    }
    if (this.draggingRoad || this.draggingRoadPoint >= 0) {
      this.draggingRoad = false;
      this.draggingRoadPoint = -1;
      this._updateRoadPanel();
    }
    this.alignGuides = [];
    this.dragging = false;
    this.resizing = false;
    this.panning = false;
    this.dragStart = null;
    this.dragOriginal = null;
    if (wasMutating) this._saveSnapshot();
  }

  _onWheel(e) {
    e.preventDefault();
    const pos = this._getCanvasPos(e);

    const bldBefore = this._toBuilding(pos.x, pos.y);
    const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
    this.zoom = Math.max(0.3, Math.min(30, this.zoom * factor));
    const bldAfter = this._toBuilding(pos.x, pos.y);

    this.pan.x -= (bldAfter.x - bldBefore.x);
    this.pan.y -= (bldAfter.z - bldBefore.z);
  }

  // ─── ADD / DELETE / TOGGLE POLYGON ───

  _addElement(typeName) {
    // Place new element at viewport center
    const center = this._toBuilding(this.canvas.width / 2, this.canvas.height / 2);
    const id = this._nextId(typeName);
    const newItem = {
      id,
      x: this._snap(center.x - 10),
      z: this._snap(center.z - 10),
      w: 20,
      d: 20,
      label: `${typeName} ${id.split('-')[1] || ''}`.trim(),
      type: typeName,
    };

    // Add to appropriate list
    const list = this.activeFloor === 'ground' ? this.data.ground : this.data.upper;
    if (typeName === 'Showroom') {
      list.showrooms.push(newItem);
      this.selected = { list: 'showrooms', index: list.showrooms.length - 1 };
    } else if (typeName === 'Office') {
      if (this.activeFloor === 'upper') {
        list.offices.push(newItem);
        this.selected = { list: 'offices', index: list.offices.length - 1 };
      } else {
        list.showrooms.push(newItem);
        this.selected = { list: 'showrooms', index: list.showrooms.length - 1 };
      }
    } else {
      // All other types go to specialAreas (ground) or showrooms (upper, as generic list)
      if (this.activeFloor === 'ground') {
        list.specialAreas.push(newItem);
        this.selected = { list: 'specialAreas', index: list.specialAreas.length - 1 };
      } else {
        list.showrooms.push(newItem);
        this.selected = { list: 'showrooms', index: list.showrooms.length - 1 };
      }
    }

    this._saveSnapshot();
    this._updatePanel();
    this._updateToolbarButtons();
  }

  _deleteSelected() {
    if (!this.selected) return;
    const list = this.activeFloor === 'ground' ? this.data.ground : this.data.upper;
    // Collect all selections and sort by index descending to avoid shift issues
    const allSel = this._getAllSelected();
    const byList = {};
    for (const sel of allSel) {
      if (!byList[sel.list]) byList[sel.list] = [];
      byList[sel.list].push(sel.index);
    }
    for (const listName of Object.keys(byList)) {
      byList[listName].sort((a, b) => b - a); // descending
      for (const idx of byList[listName]) {
        const arr = list[listName];
        if (arr && idx < arr.length) {
          // Remove from any groups referencing this item
          this.groups.forEach(g => {
            g.members = g.members.filter(m =>
              !(m.type === 'item' && m.list === listName && m.index === idx)
            );
            // Adjust indices for items after deleted index
            g.members.forEach(m => {
              if (m.type === 'item' && m.list === listName && m.index > idx) {
                m.index--;
              }
            });
          });
          // Remove empty groups
          this.groups = this.groups.filter(g => g.members.length > 0);
          arr.splice(idx, 1);
        }
      }
    }
    this.selected = null;
    this.multiSelected = [];
    this._updatePanel();
    this._updateToolbarButtons();
  }

  _togglePolygon() {
    if (!this.selected) return;
    const item = this._getSelectedItem();
    if (!item) return;

    if (this._isPolygon(item)) {
      // Convert polygon → rectangle (use AABB)
      const aabb = this._computeAABB(item.vertices);
      item.x = aabb.x;
      item.z = aabb.z;
      item.w = aabb.w;
      item.d = aabb.d;
      delete item.vertices;
      document.getElementById('ed-toggle-poly').textContent = 'To Polygon';
    } else {
      // Convert rectangle → polygon (4 corners)
      item.vertices = [
        { x: item.x,          z: item.z },
        { x: item.x + item.w, z: item.z },
        { x: item.x + item.w, z: item.z + item.d },
        { x: item.x,          z: item.z + item.d },
      ];
      document.getElementById('ed-toggle-poly').textContent = 'To Rectangle';
    }
    this.hoverHandle = -1;
    this._updatePanel();
  }

  _addParkingGroup() {
    const center = this._toBuilding(this.canvas.width / 2, this.canvas.height / 2);
    const cx = this._snap(center.x);
    const cz = this._snap(center.z);
    const id = 'PKG-' + (this.parkingGroups.length + 1);
    this.parkingGroups.push({
      id, label: 'Parking ' + (this.parkingGroups.length + 1),
      vertices: [
        { x: cx, z: cz },
        { x: cx + 20, z: cz + 34 },
        { x: cx + 13, z: cz + 38 },
        { x: cx - 7, z: cz + 4 },
      ],
      angle: 60, spotWidth: 9,
    });
    this.selectedParkingIndex = this.parkingGroups.length - 1;
    this._saveSnapshot();
    this._updateParkingPanel();
  }

  _addPlanter() {
    const center = this._toBuilding(this.canvas.width / 2, this.canvas.height / 2);
    const id = 'PL-' + (this.planters.length + 1);
    this.planters.push({
      id, label: 'Planter ' + (this.planters.length + 1),
      x: this._snap(center.x), z: this._snap(center.z),
      diameter: 5, height: 4.5, hasTree: true,
    });
    this.selectedPlanterIndex = this.planters.length - 1;
    this._saveSnapshot();
    this._updatePlanterPanel();
  }

  _deleteExteriorSelected() {
    if (this.editingParking && this.selectedParkingIndex >= 0) {
      this.parkingGroups.splice(this.selectedParkingIndex, 1);
      this.selectedParkingIndex = -1;
      this._saveSnapshot();
      this._updateParkingPanel();
    } else if (this.editingPlanters && this.selectedPlanterIndex >= 0) {
      this.planters.splice(this.selectedPlanterIndex, 1);
      this.selectedPlanterIndex = -1;
      this._saveSnapshot();
      this._updatePlanterPanel();
    } else if (this.editingRoads && this.selectedRoadIndex >= 0) {
      this.roads.splice(this.selectedRoadIndex, 1);
      this.selectedRoadIndex = -1;
      this._saveSnapshot();
      this._updateRoadPanel();
    }
  }

  _updateToolbarButtons() {
    const delBtn = document.getElementById('ed-delete');
    const polyBtn = document.getElementById('ed-toggle-poly');
    const copyBtn = document.getElementById('ed-copy');
    const pasteBtn = document.getElementById('ed-paste');
    const groupBtn = document.getElementById('ed-group');
    const ungroupBtn = document.getElementById('ed-ungroup');

    // Enable Delete for exterior selections too
    const hasExtSelection =
      (this.editingParking && this.selectedParkingIndex >= 0) ||
      (this.editingPlanters && this.selectedPlanterIndex >= 0) ||
      (this.editingRoads && this.selectedRoadIndex >= 0);

    if (this.selected) {
      delBtn.disabled = false;
      polyBtn.disabled = false;
      copyBtn.disabled = false;
      const item = this._getSelectedItem();
      polyBtn.textContent = this._isPolygon(item) ? 'To Rectangle' : 'To Polygon';
    } else if (hasExtSelection) {
      delBtn.disabled = false;
      polyBtn.disabled = true;
      copyBtn.disabled = true;
      polyBtn.textContent = 'To Polygon';
    } else {
      delBtn.disabled = true;
      polyBtn.disabled = true;
      copyBtn.disabled = true;
      polyBtn.textContent = 'To Polygon';
    }
    pasteBtn.disabled = !this.clipboard;
    // Group: enabled when 2+ items selected
    if (groupBtn) groupBtn.disabled = this._getAllSelected().length < 2;
    // Ungroup: enabled when primary selection belongs to a group
    if (ungroupBtn) ungroupBtn.disabled = !(this.selected && this._findGroupContaining(this.selected));
    this._updateUndoRedoButtons();
  }

  // ─── OUTLINE PANEL ───

  _updateOutlinePanel() {
    if (!this.editingOutline) return;
    const verts = this.outlineVertices;

    let html = `<h3>Building Outline</h3>
      <p class="ed-hint" style="margin-bottom:8px">Drag vertices to reshape. Right-click edge to add vertex, right-click vertex to remove.</p>
      <div class="ed-field ed-readonly">
        <label>Vertices</label>
        <span>${verts.length}</span>
      </div>
      <div class="ed-vertex-list" id="ed-outline-verts">
        ${verts.map((v, i) => `
          <div class="ed-vertex-row" style="${i === this.selectedOutlineVertex ? 'background:rgba(255,204,0,0.15)' : ''}">
            <span class="ed-vertex-idx">${i}</span>
            <input type="number" class="ed-vertex-input ed-ov" data-vi="${i}" data-axis="x" value="${v.x.toFixed(1)}" step="0.5" />
            <input type="number" class="ed-vertex-input ed-ov" data-vi="${i}" data-axis="z" value="${v.z.toFixed(1)}" step="0.5" />
          </div>
        `).join('')}
      </div>
    `;

    this.panel.innerHTML = html;

    // Wire outline vertex inputs
    document.querySelectorAll('.ed-ov').forEach(inp => {
      inp.addEventListener('input', () => {
        const vi = parseInt(inp.dataset.vi);
        const axis = inp.dataset.axis;
        this.outlineVertices[vi][axis] = parseFloat(inp.value) || 0;
      });
      inp.addEventListener('change', () => this._saveSnapshot());
      inp.addEventListener('focus', () => {
        this.selectedOutlineVertex = parseInt(inp.dataset.vi);
      });
    });
  }

  _updateOutlinePanelValues() {
    document.querySelectorAll('.ed-ov').forEach(inp => {
      const vi = parseInt(inp.dataset.vi);
      const axis = inp.dataset.axis;
      const v = this.outlineVertices[vi];
      if (v) inp.value = v[axis].toFixed(1);
    });
  }

  // ─── WALKWAY PANEL ───

  _updateWalkwayPanel() {
    if (!this.editingWalkway) return;
    const verts = this.walkwayVertices;

    let html = `<h3>Walkway Outline</h3>
      <p class="ed-hint" style="margin-bottom:8px">Drag vertices to reshape. Right-click edge to add vertex, right-click vertex to remove.</p>
      <div class="ed-field ed-readonly">
        <label>Vertices</label>
        <span>${verts.length}</span>
      </div>
      <div class="ed-vertex-list" id="ed-walkway-verts">
        ${verts.map((v, i) => `
          <div class="ed-vertex-row" style="${i === this.selectedWalkwayVertex ? 'background:rgba(0,204,255,0.15)' : ''}">
            <span class="ed-vertex-idx">${i}</span>
            <input type="number" class="ed-vertex-input ed-wv" data-vi="${i}" data-axis="x" value="${v.x.toFixed(1)}" step="0.5" />
            <input type="number" class="ed-vertex-input ed-wv" data-vi="${i}" data-axis="z" value="${v.z.toFixed(1)}" step="0.5" />
          </div>
        `).join('')}
      </div>
    `;

    this.panel.innerHTML = html;

    // Wire walkway vertex inputs
    document.querySelectorAll('.ed-wv').forEach(inp => {
      inp.addEventListener('input', () => {
        const vi = parseInt(inp.dataset.vi);
        const axis = inp.dataset.axis;
        this.walkwayVertices[vi][axis] = parseFloat(inp.value) || 0;
      });
      inp.addEventListener('change', () => this._saveSnapshot());
      inp.addEventListener('focus', () => {
        this.selectedWalkwayVertex = parseInt(inp.dataset.vi);
      });
    });
  }

  _updateWalkwayPanelValues() {
    document.querySelectorAll('.ed-wv').forEach(inp => {
      const vi = parseInt(inp.dataset.vi);
      const axis = inp.dataset.axis;
      const v = this.walkwayVertices[vi];
      if (v) inp.value = v[axis].toFixed(1);
    });
  }

  // ─── STAIR PANEL ───

  _updateStairPanel() {
    if (!this.editingStair) return;
    const verts = this.stairVertices;

    let html = `<h3>Stair Outline</h3>
      <p class="ed-hint" style="margin-bottom:8px">Drag vertices to reshape. Right-click edge to add vertex, right-click vertex to remove.</p>
      <div class="ed-field">
        <label>Steps</label>
        <input type="number" id="ed-stair-steps" value="${this.stairNumSteps}" min="2" max="20" step="1" style="width:60px" />
      </div>
      <div class="ed-field ed-readonly">
        <label>Vertices</label>
        <span>${verts.length}</span>
      </div>
      <div class="ed-vertex-list" id="ed-stair-verts">
        ${verts.map((v, i) => `
          <div class="ed-vertex-row" style="${i === this.selectedStairVertex ? 'background:rgba(255,136,68,0.15)' : ''}">
            <span class="ed-vertex-idx">${i}</span>
            <input type="number" class="ed-vertex-input ed-sv" data-vi="${i}" data-axis="x" value="${v.x.toFixed(1)}" step="0.5" />
            <input type="number" class="ed-vertex-input ed-sv" data-vi="${i}" data-axis="z" value="${v.z.toFixed(1)}" step="0.5" />
          </div>
        `).join('')}
      </div>
    `;

    this.panel.innerHTML = html;

    // Wire stair steps input
    const stepsInp = document.getElementById('ed-stair-steps');
    if (stepsInp) {
      stepsInp.addEventListener('change', () => {
        this.stairNumSteps = Math.max(2, Math.min(20, parseInt(stepsInp.value) || 5));
        stepsInp.value = this.stairNumSteps;
        this._saveSnapshot();
      });
    }

    // Wire stair vertex inputs
    document.querySelectorAll('.ed-sv').forEach(inp => {
      inp.addEventListener('input', () => {
        const vi = parseInt(inp.dataset.vi);
        const axis = inp.dataset.axis;
        this.stairVertices[vi][axis] = parseFloat(inp.value) || 0;
      });
      inp.addEventListener('change', () => this._saveSnapshot());
      inp.addEventListener('focus', () => {
        this.selectedStairVertex = parseInt(inp.dataset.vi);
      });
    });
  }

  _updateStairPanelValues() {
    document.querySelectorAll('.ed-sv').forEach(inp => {
      const vi = parseInt(inp.dataset.vi);
      const axis = inp.dataset.axis;
      const v = this.stairVertices[vi];
      if (v) inp.value = v[axis].toFixed(1);
    });
  }

  // ─── PROPERTY PANEL ───

  _updatePanel() {
    if (this.editingOutline) {
      this._updateOutlinePanel();
      return;
    }
    if (this.editingWalkway) {
      this._updateWalkwayPanel();
      return;
    }
    if (this.editingStair) {
      this._updateStairPanel();
      return;
    }
    if (this.editingGroundPad) {
      this._updateGroundPadPanel();
      return;
    }
    if (this.editingParking) {
      this._updateParkingPanel();
      return;
    }
    if (this.editingPlanters) {
      this._updatePlanterPanel();
      return;
    }

    const sel = this._getSelectedItem();
    if (!sel) {
      this.panel.innerHTML = '<p class="ed-hint">Click a unit to select it</p>';
      return;
    }

    const isPoly = this._isPolygon(sel);
    const area = isPoly ? this._polygonArea(sel.vertices) : (sel.w * sel.d);

    // Type dropdown options
    const allTypes = Object.keys(ELEMENT_TYPES);
    const typeOpts = allTypes.map(t =>
      `<option value="${t}" ${(sel.type || '') === t ? 'selected' : ''}>${t}</option>`
    ).join('');

    let html = `
      <div class="ed-field">
        <label>Type</label>
        <select class="ed-select" id="ed-p-type">${typeOpts}</select>
      </div>
      <div class="ed-field">
        <label>Label</label>
        <input type="text" id="ed-p-label" value="${sel.label || ''}" />
      </div>
    `;

    if (isPoly) {
      html += `
        <div class="ed-field ed-readonly">
          <label>Area</label>
          <span id="ed-area-display">${area.toFixed(0)} sq ft</span>
        </div>
        <div class="ed-field">
          <label>Vertices</label>
          <div class="ed-vertex-list" id="ed-vertex-list">
            ${sel.vertices.map((v, i) => `
              <div class="ed-vertex-row">
                <span class="ed-vertex-idx">${i}</span>
                <input type="number" class="ed-vertex-input" data-vi="${i}" data-axis="x" value="${v.x.toFixed(2)}" step="0.5" />
                <input type="number" class="ed-vertex-input" data-vi="${i}" data-axis="z" value="${v.z.toFixed(2)}" step="0.5" />
              </div>
            `).join('')}
          </div>
        </div>
      `;
    } else {
      html += `
        <div class="ed-field">
          <label>X (East-West)</label>
          <input type="number" id="ed-p-x" value="${sel.x}" step="0.5" />
        </div>
        <div class="ed-field">
          <label>Z (North-South)</label>
          <input type="number" id="ed-p-z" value="${sel.z}" step="0.5" />
        </div>
        <div class="ed-field">
          <label>Width (E-W)</label>
          <input type="number" id="ed-p-w" value="${sel.w}" step="0.5" min="1" />
        </div>
        <div class="ed-field">
          <label>Depth (N-S)</label>
          <input type="number" id="ed-p-d" value="${sel.d}" step="0.5" min="1" />
        </div>
        <div class="ed-field ed-readonly">
          <label>Area</label>
          <span id="ed-area-display">${area.toFixed(0)} sq ft</span>
        </div>
      `;
    }

    html += `
      <div class="ed-field ed-readonly">
        <label>ID</label>
        <span>${sel.id}</span>
      </div>
    `;

    // Multi-select info
    if (this.multiSelected.length > 0) {
      html += `<div class="ed-field ed-readonly" style="border-top:1px solid rgba(255,255,255,0.1);padding-top:6px;margin-top:6px">
        <label>Selected</label>
        <span>${this._getAllSelected().length} items</span>
      </div>`;
    }

    // Group membership info
    if (this.selected) {
      const grp = this._findGroupContaining(this.selected);
      if (grp) {
        const memberCount = this._getGroupMembers(grp).length;
        html += `<div style="border-top:1px solid rgba(255,136,68,0.3);padding-top:6px;margin-top:6px">
          <div class="ed-field ed-readonly">
            <label>Group</label>
            <span style="color:#ff8844">${grp.label} (${memberCount} items)</span>
          </div>
        </div>`;
      }
    }

    this.panel.innerHTML = html;

    // Wire inputs
    // Type dropdown
    const typeEl = document.getElementById('ed-p-type');
    if (typeEl) {
      typeEl.addEventListener('change', () => {
        const item = this._getSelectedItem();
        if (item) { item.type = typeEl.value; this._saveSnapshot(); }
      });
    }

    // Label
    const labelEl = document.getElementById('ed-p-label');
    if (labelEl) {
      labelEl.addEventListener('input', () => {
        const item = this._getSelectedItem();
        if (item) item.label = labelEl.value;
      });
      labelEl.addEventListener('change', () => this._saveSnapshot());
    }

    if (isPoly) {
      // Vertex inputs
      document.querySelectorAll('.ed-vertex-input').forEach(inp => {
        inp.addEventListener('input', () => {
          const item = this._getSelectedItem();
          if (!item || !item.vertices) return;
          const vi = parseInt(inp.dataset.vi);
          const axis = inp.dataset.axis;
          item.vertices[vi][axis] = parseFloat(inp.value) || 0;
          const aabb = this._computeAABB(item.vertices);
          item.x = aabb.x; item.z = aabb.z; item.w = aabb.w; item.d = aabb.d;
          const areaDisplay = document.getElementById('ed-area-display');
          if (areaDisplay) areaDisplay.textContent = `${this._polygonArea(item.vertices).toFixed(0)} sq ft`;
        });
        inp.addEventListener('change', () => this._saveSnapshot());
      });
    } else {
      // Rect inputs
      const bind = (id, prop) => {
        const el = document.getElementById(id);
        if (!el) return;
        el.addEventListener('input', () => {
          const item = this._getSelectedItem();
          if (!item) return;
          item[prop] = parseFloat(el.value) || 0;
          const areaDisplay = document.getElementById('ed-area-display');
          if (areaDisplay) areaDisplay.textContent = `${(item.w * item.d).toFixed(0)} sq ft`;
        });
        el.addEventListener('change', () => this._saveSnapshot());
      };
      bind('ed-p-x', 'x');
      bind('ed-p-z', 'z');
      bind('ed-p-w', 'w');
      bind('ed-p-d', 'd');
    }
  }

  _updatePanelValues() {
    const sel = this._getSelectedItem();
    if (!sel) return;

    if (this._isPolygon(sel)) {
      // Update vertex inputs
      document.querySelectorAll('.ed-vertex-input').forEach(inp => {
        const vi = parseInt(inp.dataset.vi);
        const axis = inp.dataset.axis;
        if (sel.vertices[vi]) {
          inp.value = sel.vertices[vi][axis].toFixed(2);
        }
      });
      const areaDisplay = document.getElementById('ed-area-display');
      if (areaDisplay) areaDisplay.textContent = `${this._polygonArea(sel.vertices).toFixed(0)} sq ft`;
    } else {
      const setVal = (id, v) => {
        const el = document.getElementById(id);
        if (el) el.value = typeof v === 'number' ? v.toFixed(2) : v;
      };
      setVal('ed-p-x', sel.x);
      setVal('ed-p-z', sel.z);
      setVal('ed-p-w', sel.w);
      setVal('ed-p-d', sel.d);
      const areaDisplay = document.getElementById('ed-area-display');
      if (areaDisplay) areaDisplay.textContent = `${(sel.w * sel.d).toFixed(0)} sq ft`;
    }
  }

  // ─── GROUND PAD PANEL ───

  _updateGroundPadPanel() {
    if (!this.editingGroundPad) return;
    const verts = this.groundPadVertices;

    let html = `<h3>Ground Pad</h3>
      <p class="ed-hint" style="margin-bottom:8px">Drag vertices to reshape. Right-click edge to add vertex, right-click vertex to remove.</p>
      <div class="ed-field ed-readonly">
        <label>Vertices</label>
        <span>${verts.length}</span>
      </div>
      <div class="ed-vertex-list" id="ed-gpad-verts">
        ${verts.map((v, i) => `
          <div class="ed-vertex-row" style="${i === this.selectedGroundPadVertex ? 'background:rgba(68,204,68,0.15)' : ''}">
            <span class="ed-vertex-idx">${i}</span>
            <input type="number" class="ed-vertex-input ed-gpv" data-vi="${i}" data-axis="x" value="${v.x.toFixed(1)}" step="0.5" />
            <input type="number" class="ed-vertex-input ed-gpv" data-vi="${i}" data-axis="z" value="${v.z.toFixed(1)}" step="0.5" />
          </div>
        `).join('')}
      </div>
    `;

    this.panel.innerHTML = html;

    document.querySelectorAll('.ed-gpv').forEach(inp => {
      inp.addEventListener('input', () => {
        const vi = parseInt(inp.dataset.vi);
        const axis = inp.dataset.axis;
        this.groundPadVertices[vi][axis] = parseFloat(inp.value) || 0;
      });
      inp.addEventListener('change', () => this._saveSnapshot());
      inp.addEventListener('focus', () => {
        this.selectedGroundPadVertex = parseInt(inp.dataset.vi);
      });
    });
  }

  _updateGroundPadPanelValues() {
    document.querySelectorAll('.ed-gpv').forEach(inp => {
      const vi = parseInt(inp.dataset.vi);
      const axis = inp.dataset.axis;
      const v = this.groundPadVertices[vi];
      if (v) inp.value = v[axis].toFixed(1);
    });
  }

  // ─── PARKING PANEL ───

  _updateParkingPanel() {
    if (!this.editingParking) return;

    if (this.selectedParkingIndex < 0) {
      this.panel.innerHTML = `<h3>Parking Groups</h3>
        <p class="ed-hint">Click a parking group to select it. ${this.parkingGroups.length} groups total.</p>`;
      return;
    }

    const grp = this.parkingGroups[this.selectedParkingIndex];
    if (!grp) return;

    let html = `<h3>Parking: ${grp.label}</h3>
      <div class="ed-field">
        <label>Label</label>
        <input type="text" id="ed-pkg-label" value="${grp.label || ''}" />
      </div>
      <div class="ed-field">
        <label>Angle (°)</label>
        <input type="number" id="ed-pkg-angle" value="${grp.angle || 60}" min="30" max="90" step="5" style="width:60px" />
      </div>
      <div class="ed-field">
        <label>Spot Width (ft)</label>
        <input type="number" id="ed-pkg-spot" value="${grp.spotWidth || 9}" min="6" max="14" step="0.5" style="width:60px" />
      </div>
      <div class="ed-field ed-readonly">
        <label>Vertices</label>
        <span>${grp.vertices ? grp.vertices.length : 0}</span>
      </div>
      <div class="ed-vertex-list" id="ed-pkg-verts">
        ${(grp.vertices || []).map((v, i) => `
          <div class="ed-vertex-row">
            <span class="ed-vertex-idx">${i}</span>
            <input type="number" class="ed-vertex-input ed-pkv" data-vi="${i}" data-axis="x" value="${v.x.toFixed(1)}" step="0.5" />
            <input type="number" class="ed-vertex-input ed-pkv" data-vi="${i}" data-axis="z" value="${v.z.toFixed(1)}" step="0.5" />
          </div>
        `).join('')}
      </div>
      <div class="ed-field ed-readonly">
        <label>ID</label>
        <span>${grp.id}</span>
      </div>
    `;

    this.panel.innerHTML = html;

    // Wire inputs
    const labelEl = document.getElementById('ed-pkg-label');
    if (labelEl) {
      labelEl.addEventListener('input', () => { grp.label = labelEl.value; });
      labelEl.addEventListener('change', () => this._saveSnapshot());
    }
    const angleEl = document.getElementById('ed-pkg-angle');
    if (angleEl) {
      angleEl.addEventListener('change', () => {
        grp.angle = Math.max(30, Math.min(90, parseFloat(angleEl.value) || 60));
        angleEl.value = grp.angle;
        this._saveSnapshot();
      });
    }
    const spotEl = document.getElementById('ed-pkg-spot');
    if (spotEl) {
      spotEl.addEventListener('change', () => {
        grp.spotWidth = Math.max(6, Math.min(14, parseFloat(spotEl.value) || 9));
        spotEl.value = grp.spotWidth;
        this._saveSnapshot();
      });
    }
    document.querySelectorAll('.ed-pkv').forEach(inp => {
      inp.addEventListener('input', () => {
        const vi = parseInt(inp.dataset.vi);
        const axis = inp.dataset.axis;
        if (grp.vertices[vi]) grp.vertices[vi][axis] = parseFloat(inp.value) || 0;
      });
      inp.addEventListener('change', () => this._saveSnapshot());
    });
  }

  _updateParkingPanelValues() {
    if (this.selectedParkingIndex < 0) return;
    const grp = this.parkingGroups[this.selectedParkingIndex];
    if (!grp || !grp.vertices) return;
    document.querySelectorAll('.ed-pkv').forEach(inp => {
      const vi = parseInt(inp.dataset.vi);
      const axis = inp.dataset.axis;
      if (grp.vertices[vi]) inp.value = grp.vertices[vi][axis].toFixed(1);
    });
  }

  // ─── PLANTER PANEL ───

  _updatePlanterPanel() {
    if (!this.editingPlanters) return;

    if (this.selectedPlanterIndex < 0) {
      this.panel.innerHTML = `<h3>Planters</h3>
        <p class="ed-hint">Click a planter to select it. ${this.planters.length} planters total.</p>`;
      return;
    }

    const pl = this.planters[this.selectedPlanterIndex];
    if (!pl) return;

    const shape = pl.shape || 'round';
    const isRect = shape === 'rect';

    let html = `<h3>Planter: ${pl.label}</h3>
      <div class="ed-field">
        <label>Label</label>
        <input type="text" id="ed-pl-label" value="${pl.label || ''}" />
      </div>
      <div class="ed-field">
        <label>Shape</label>
        <select id="ed-pl-shape">
          <option value="round" ${!isRect ? 'selected' : ''}>Round</option>
          <option value="rect" ${isRect ? 'selected' : ''}>Rectangle</option>
        </select>
      </div>
      <div class="ed-field">
        <label>Center X</label>
        <input type="number" id="ed-pl-x" value="${pl.x.toFixed(1)}" step="0.5" />
      </div>
      <div class="ed-field">
        <label>Center Z</label>
        <input type="number" id="ed-pl-z" value="${pl.z.toFixed(1)}" step="0.5" />
      </div>
      <div class="ed-field" id="ed-pl-diam-row" style="display:${isRect ? 'none' : ''}">
        <label>Diameter (ft)</label>
        <input type="number" id="ed-pl-diam" value="${(pl.diameter || 5).toFixed(1)}" min="2" max="20" step="0.5" />
      </div>
      <div class="ed-field" id="ed-pl-width-row" style="display:${isRect ? '' : 'none'}">
        <label>Width (ft)</label>
        <input type="number" id="ed-pl-width" value="${(pl.width || pl.diameter || 5).toFixed(1)}" min="2" max="100" step="0.5" />
      </div>
      <div class="ed-field" id="ed-pl-depth-row" style="display:${isRect ? '' : 'none'}">
        <label>Depth (ft)</label>
        <input type="number" id="ed-pl-depth" value="${(pl.depth || pl.diameter || 5).toFixed(1)}" min="2" max="100" step="0.5" />
      </div>
      <div class="ed-field" id="ed-pl-angle-row" style="display:${isRect ? '' : 'none'}">
        <label>Angle (deg)</label>
        <input type="number" id="ed-pl-angle" value="${(pl.angle || 0).toFixed(1)}" step="1" />
      </div>
      <div class="ed-field">
        <label>Height (ft)</label>
        <input type="number" id="ed-pl-h" value="${(pl.height || 4.5).toFixed(1)}" min="1" max="10" step="0.5" />
      </div>
      <div class="ed-field">
        <label>Has Tree</label>
        <input type="checkbox" id="ed-pl-tree" ${pl.hasTree ? 'checked' : ''} />
      </div>
      <div class="ed-field ed-readonly">
        <label>ID</label>
        <span>${pl.id}</span>
      </div>
    `;

    this.panel.innerHTML = html;

    // Wire inputs
    const labelEl = document.getElementById('ed-pl-label');
    if (labelEl) {
      labelEl.addEventListener('input', () => { pl.label = labelEl.value; });
      labelEl.addEventListener('change', () => this._saveSnapshot());
    }
    const bind = (id, prop, parse) => {
      const el = document.getElementById(id);
      if (!el) return;
      el.addEventListener('input', () => { pl[prop] = (parse || parseFloat)(el.value) || 0; });
      el.addEventListener('change', () => this._saveSnapshot());
    };
    bind('ed-pl-x', 'x');
    bind('ed-pl-z', 'z');
    bind('ed-pl-diam', 'diameter');
    bind('ed-pl-width', 'width');
    bind('ed-pl-depth', 'depth');
    bind('ed-pl-angle', 'angle');
    bind('ed-pl-h', 'height');

    // Shape dropdown
    const shapeEl = document.getElementById('ed-pl-shape');
    if (shapeEl) {
      shapeEl.addEventListener('change', () => {
        pl.shape = shapeEl.value;
        if (pl.shape === 'rect' && !pl.width) {
          pl.width = pl.diameter || 5;
          pl.depth = pl.diameter || 5;
          pl.angle = pl.angle || 0;
        }
        this._saveSnapshot();
        this._updatePlanterPanel(); // rebuild panel to toggle fields
      });
    }

    const treeEl = document.getElementById('ed-pl-tree');
    if (treeEl) {
      treeEl.addEventListener('change', () => {
        pl.hasTree = treeEl.checked;
        this._saveSnapshot();
      });
    }
  }

  _updatePlanterPanelValues() {
    if (this.selectedPlanterIndex < 0) return;
    const pl = this.planters[this.selectedPlanterIndex];
    if (!pl) return;
    const setVal = (id, v) => {
      const el = document.getElementById(id);
      if (el) el.value = typeof v === 'number' ? v.toFixed(1) : v;
    };
    setVal('ed-pl-x', pl.x);
    setVal('ed-pl-z', pl.z);
    setVal('ed-pl-diam', pl.diameter || 5);
    setVal('ed-pl-width', pl.width || pl.diameter || 5);
    setVal('ed-pl-depth', pl.depth || pl.diameter || 5);
    setVal('ed-pl-angle', pl.angle || 0);
    setVal('ed-pl-h', pl.height || 4.5);
  }

  // ─── ALIGNMENT GUIDES ───

  _collectAlignmentTargets(excludeType, excludeIndex) {
    const targets = [];

    // Planter centers
    this.planters.forEach((pl, i) => {
      if (excludeType === 'planter' && i === excludeIndex) return;
      targets.push({ x: pl.x, z: pl.z, label: pl.label || pl.id });
    });

    // Parking group centers
    this.parkingGroups.forEach((grp, i) => {
      if (excludeType === 'parking' && i === excludeIndex) return;
      if (!grp.vertices || grp.vertices.length < 2) return;
      const cx = grp.vertices.reduce((s, v) => s + v.x, 0) / grp.vertices.length;
      const cz = grp.vertices.reduce((s, v) => s + v.z, 0) / grp.vertices.length;
      targets.push({ x: cx, z: cz, label: grp.label || grp.id });
    });

    // Road control points
    this.roads.forEach((rd, i) => {
      if (excludeType === 'road' && i === excludeIndex) return;
      if (!rd.points) return;
      rd.points.forEach(p => targets.push({ x: p.x, z: p.z, label: rd.label || rd.id }));
    });

    // Building outline vertices
    this.outlineVertices.forEach(v => {
      targets.push({ x: v.x, z: v.z, label: 'Outline' });
    });

    return targets;
  }

  _computeAlignGuides(dragX, dragZ, excludeType, excludeIdx) {
    const targets = this._collectAlignmentTargets(excludeType, excludeIdx);
    const threshold = 3; // 3ft
    const guides = [];
    const seenX = new Set(), seenZ = new Set();
    for (const t of targets) {
      if (Math.abs(dragX - t.x) < threshold && !seenX.has(t.x)) {
        guides.push({ axis: 'x', value: t.x, label: t.label });
        seenX.add(t.x);
      }
      if (Math.abs(dragZ - t.z) < threshold && !seenZ.has(t.z)) {
        guides.push({ axis: 'z', value: t.z, label: t.label });
        seenZ.add(t.z);
      }
    }
    return guides;
  }

  // ─── EDGE MEASUREMENTS ───

  /**
   * Draw dimension labels on each edge of a closed or open polygon.
   * @param {Array<{x,z}>} verts - vertices in building coords
   * @param {boolean} closed - true to also measure last→first edge
   * @param {string} color - label text color
   * @param {number} [offsetPx=14] - pixel offset outward from edge midpoint
   * @param {object} [source] - optional {type, index} for click-to-edit hit areas
   */
  _drawEdgeMeasurements(verts, closed, color, offsetPx = 14, source = null) {
    if (!verts || verts.length < 2) return;
    const ctx = this.ctx;
    const edgeCount = closed ? verts.length : verts.length - 1;

    for (let i = 0; i < edgeCount; i++) {
      const a = verts[i];
      const b = verts[(i + 1) % verts.length];

      const dx = b.x - a.x;
      const dz = b.z - a.z;
      const len = Math.hypot(dx, dz);
      if (len < 0.5) continue;

      const sa = this._toScreen(a.x, a.z);
      const sb = this._toScreen(b.x, b.z);
      const mx = (sa.x + sb.x) / 2;
      const my = (sa.y + sb.y) / 2;
      const edx = sb.x - sa.x;
      const edy = sb.y - sa.y;
      const eLen = Math.hypot(edx, edy);
      if (eLen < 4) continue;

      let nx = -edy / eLen;
      let ny = edx / eLen;
      if (closed && verts.length >= 3) {
        let cx = 0, cz = 0;
        for (const v of verts) { cx += v.x; cz += v.z; }
        cx /= verts.length; cz /= verts.length;
        const sc = this._toScreen(cx, cz);
        const toCenter = (sc.x - mx) * nx + (sc.y - my) * ny;
        if (toCenter > 0) { nx = -nx; ny = -ny; }
      }

      const lx = mx + nx * offsetPx;
      const ly = my + ny * offsetPx;
      const label = len.toFixed(1) + ' ft';
      const angle = Math.atan2(edy, edx);

      ctx.save();
      ctx.translate(lx, ly);
      let rot = angle;
      if (rot > Math.PI / 2) rot -= Math.PI;
      if (rot < -Math.PI / 2) rot += Math.PI;
      ctx.rotate(rot);

      ctx.font = 'bold 10px sans-serif';
      const tw = ctx.measureText(label).width;
      const pad = 3;
      const bw = tw + pad * 2;
      const bh = 14;
      ctx.fillStyle = 'rgba(0,0,0,0.8)';
      ctx.fillRect(-bw / 2, -bh / 2, bw, bh);
      ctx.fillStyle = color;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, 0, 0);
      ctx.restore();

      // Register hit area for click-to-edit
      if (source) {
        this.measurementHitAreas.push({
          sx: lx, sy: ly, hw: bw / 2 + 4, hh: bh / 2 + 4, rot,
          length: len, edgeIndex: i, viA: i, viB: (i + 1) % verts.length,
          ...source,
        });
      }

      // Tick marks
      ctx.strokeStyle = color;
      ctx.globalAlpha = 0.5;
      ctx.lineWidth = 1;
      const tickLen = 4;
      ctx.beginPath();
      ctx.moveTo(sa.x + nx * (offsetPx - tickLen), sa.y + ny * (offsetPx - tickLen));
      ctx.lineTo(sa.x + nx * (offsetPx + tickLen), sa.y + ny * (offsetPx + tickLen));
      ctx.moveTo(sb.x + nx * (offsetPx - tickLen), sb.y + ny * (offsetPx - tickLen));
      ctx.lineTo(sb.x + nx * (offsetPx + tickLen), sb.y + ny * (offsetPx + tickLen));
      ctx.stroke();
      ctx.globalAlpha = 1.0;
    }
  }

  /**
   * Draw dimension labels for a planter (diameter or width/depth).
   */
  _drawPlanterMeasurements(pl, planterIndex) {
    const ctx = this.ctx;
    const sc = this._toScreen(pl.x, pl.z);
    const isRect = (pl.shape || 'round') === 'rect';
    const color = '#88ee88';

    if (isRect) {
      const w = pl.width || pl.diameter || 5;
      const d = pl.depth || pl.diameter || 5;
      const ang = (pl.angle || 0) * Math.PI / 180;
      const cos = Math.cos(ang), sin = Math.sin(ang);
      const hw = w / 2, hd = d / 2;
      const corners = [
        { x: pl.x + (-hw * cos - (-hd) * sin), z: pl.z + (-hw * sin + (-hd) * cos) },
        { x: pl.x + (hw * cos - (-hd) * sin),  z: pl.z + (hw * sin + (-hd) * cos) },
        { x: pl.x + (hw * cos - hd * sin),      z: pl.z + (hw * sin + hd * cos) },
        { x: pl.x + (-hw * cos - hd * sin),     z: pl.z + (-hw * sin + hd * cos) },
      ];
      // edges 0,2 = width sides; edges 1,3 = depth sides
      this._drawEdgeMeasurements(corners, true, color, 16, { type: 'planterRect', index: planterIndex, verts: corners });
    } else {
      // Round planter — diameter label
      const diam = pl.diameter || 5;
      const r = diam / 2 * this.zoom;
      const label = diam.toFixed(1) + ' ft';

      ctx.save();
      ctx.font = 'bold 10px sans-serif';
      const tw = ctx.measureText(label).width;
      const lx = sc.x;
      const ly = sc.y - r - 14;

      ctx.strokeStyle = color;
      ctx.globalAlpha = 0.4;
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 2]);
      ctx.beginPath();
      ctx.moveTo(sc.x - r, sc.y);
      ctx.lineTo(sc.x + r, sc.y);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1.0;

      const pad = 3;
      const bw = tw + pad * 2;
      ctx.fillStyle = 'rgba(0,0,0,0.8)';
      ctx.fillRect(lx - bw / 2, ly - 7, bw, 14);
      ctx.fillStyle = color;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, lx, ly);
      ctx.restore();

      // Hit area for diameter
      this.measurementHitAreas.push({
        sx: lx, sy: ly, hw: bw / 2 + 4, hh: 11, rot: 0,
        type: 'planterRound', index: planterIndex, length: diam, prop: 'diameter',
      });
    }
  }

  /**
   * Draw measurements for a road: segment lengths between control points and width.
   */
  _drawRoadMeasurements(rd, roadIndex) {
    if (!rd.points || rd.points.length < 2) return;
    const ctx = this.ctx;
    const color = '#ffcc00';

    // Segment lengths between control points
    this._drawEdgeMeasurements(rd.points, false, color, 18, { type: 'road', index: roadIndex, verts: rd.points });

    // Width label at midpoint
    const midIdx = Math.floor(rd.points.length / 2);
    const a = rd.points[midIdx - 1] || rd.points[0];
    const b = rd.points[midIdx] || rd.points[1];
    const mx = (a.x + b.x) / 2;
    const mz = (a.z + b.z) / 2;

    const dx = b.x - a.x, dz = b.z - a.z;
    const segLen = Math.hypot(dx, dz);
    if (segLen < 1) return;
    const nx = -dz / segLen, nz = dx / segLen;
    const hw = (rd.width || 30) / 2;

    const sA = this._toScreen(mx + nx * hw, mz + nz * hw);
    const sB = this._toScreen(mx - nx * hw, mz - nz * hw);

    ctx.strokeStyle = 'rgba(255,204,0,0.4)';
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 2]);
    ctx.beginPath();
    ctx.moveTo(sA.x, sA.y);
    ctx.lineTo(sB.x, sB.y);
    ctx.stroke();
    ctx.setLineDash([]);

    const wMx = (sA.x + sB.x) / 2;
    const wMy = (sA.y + sB.y) / 2;
    const wLabel = (rd.width || 30).toFixed(0) + ' ft wide';

    ctx.save();
    const wAngle = Math.atan2(sB.y - sA.y, sB.x - sA.x);
    ctx.translate(wMx, wMy);
    let rot = wAngle;
    if (rot > Math.PI / 2) rot -= Math.PI;
    if (rot < -Math.PI / 2) rot += Math.PI;
    ctx.rotate(rot);

    ctx.font = 'bold 10px sans-serif';
    const tw = ctx.measureText(wLabel).width;
    const bw = tw + 6;
    ctx.fillStyle = 'rgba(0,0,0,0.8)';
    ctx.fillRect(-bw / 2, -7, bw, 14);
    ctx.fillStyle = '#ffaa00';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(wLabel, 0, 0);
    ctx.restore();

    // Hit area for road width label
    this.measurementHitAreas.push({
      sx: wMx, sy: wMy, hw: bw / 2 + 4, hh: 11, rot,
      type: 'roadWidth', index: roadIndex, length: rd.width || 30, prop: 'width',
    });
  }

  // ─── MEASUREMENT CLICK-TO-EDIT ───

  _hitTestMeasurement(sx, sy) {
    for (let i = this.measurementHitAreas.length - 1; i >= 0; i--) {
      const h = this.measurementHitAreas[i];
      // Rotate click point into label's local frame
      const dx = sx - h.sx;
      const dy = sy - h.sy;
      const cos = Math.cos(-h.rot), sin = Math.sin(-h.rot);
      const lx = dx * cos - dy * sin;
      const ly = dx * sin + dy * cos;
      if (Math.abs(lx) <= h.hw && Math.abs(ly) <= h.hh) return h;
    }
    return null;
  }

  _showMeasurementInput(hit) {
    this._dismissMeasurementInput();
    const container = this.canvas.parentElement || document.getElementById('editor-main');
    const canvasRect = this.canvas.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    const input = document.createElement('input');
    input.type = 'number';
    input.step = '0.5';
    input.value = hit.length.toFixed(1);
    input.style.cssText = `
      position: absolute;
      left: ${hit.sx - 40 + (canvasRect.left - containerRect.left)}px;
      top: ${hit.sy - 12 + (canvasRect.top - containerRect.top)}px;
      width: 80px;
      height: 24px;
      background: #1a1a3a;
      border: 2px solid #c9a94e;
      color: #fff;
      font-size: 12px;
      font-weight: bold;
      text-align: center;
      border-radius: 4px;
      z-index: 600;
      outline: none;
      pointer-events: auto;
    `;

    let committed = false;
    const apply = () => {
      if (committed) return;
      committed = true;
      const val = parseFloat(input.value);
      if (!isNaN(val) && val > 0) {
        this._saveSnapshot();
        this._applyMeasurementEdit(hit, val);
      }
      this._dismissMeasurementInput();
    };

    // Prevent all mouse events from reaching the canvas
    const stopMouse = (e) => e.stopPropagation();
    input.addEventListener('mousedown', stopMouse);
    input.addEventListener('mouseup', stopMouse);
    input.addEventListener('click', stopMouse);
    input.addEventListener('dblclick', stopMouse);
    input.addEventListener('pointerdown', stopMouse);

    input.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') { e.preventDefault(); apply(); }
      if (e.key === 'Escape') { e.preventDefault(); this._dismissMeasurementInput(); }
    });

    input.addEventListener('blur', () => {
      setTimeout(() => { if (!committed) apply(); }, 200);
    });

    container.style.position = 'relative';
    container.appendChild(input);
    this._measurementInput = input;

    // Defer focus to after the current mousedown/mouseup cycle completes
    requestAnimationFrame(() => {
      if (this._measurementInput === input) {
        input.focus();
        input.select();
      }
    });
  }

  _dismissMeasurementInput() {
    if (this._measurementInput) {
      this._measurementInput.remove();
      this._measurementInput = null;
    }
  }

  _applyMeasurementEdit(hit, newLength) {
    const { type, index, viA, viB, edgeIndex } = hit;

    if (type === 'planterRound') {
      // Edit round planter diameter
      this.planters[index].diameter = newLength;
      this._updatePlanterPanel();
      return;
    }

    if (type === 'planterRect') {
      // Rect planter: edges 0,2 are width sides; edges 1,3 are depth sides
      const pl = this.planters[index];
      const ei = edgeIndex;
      if (ei === 0 || ei === 2) {
        pl.width = newLength;
      } else {
        pl.depth = newLength;
      }
      this._updatePlanterPanel();
      return;
    }

    if (type === 'roadWidth') {
      // Edit road width
      this.roads[index].width = newLength;
      this._updateRoadPanel();
      return;
    }

    // Polygon edge types: outline, walkway, stair, groundPad, parking, road
    let verts;
    if (type === 'outline') verts = this.outlineVertices;
    else if (type === 'walkway') verts = this.walkwayVertices;
    else if (type === 'stair') verts = this.stairVertices;
    else if (type === 'groundPad') verts = this.groundPadVertices;
    else if (type === 'parking') verts = this.parkingGroups[index].vertices;
    else if (type === 'road') verts = this.roads[index].points;
    else return;

    if (!verts || viA == null || viB == null) return;
    const a = verts[viA];
    const b = verts[viB];
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const oldLen = Math.hypot(dx, dz);
    if (oldLen < 0.01) return;

    // Scale: move vertex B along the edge direction to achieve newLength
    const scale = newLength / oldLen;
    b.x = a.x + dx * scale;
    b.z = a.z + dz * scale;

    // Refresh panels
    if (type === 'outline') this._updateOutlinePanel();
    else if (type === 'walkway') this._updateWalkwayPanel();
    else if (type === 'stair') this._updateStairPanel();
    else if (type === 'groundPad') this._updateGroundPadPanel();
    else if (type === 'parking') this._updateParkingPanelValues();
    else if (type === 'road') this._updateRoadPanelValues();
  }

  _drawAlignGuides() {
    if (!this.alignGuides.length) return;
    const ctx = this.ctx;
    ctx.setLineDash([4, 4]);
    ctx.lineWidth = 1;
    this.alignGuides.forEach(g => {
      if (g.axis === 'x') {
        const sx = this._toScreen(g.value, 0).x;
        ctx.strokeStyle = '#ff44ff'; // magenta
        ctx.beginPath();
        ctx.moveTo(sx, 0);
        ctx.lineTo(sx, this.canvas.height);
        ctx.stroke();
      } else {
        const sy = this._toScreen(0, g.value).y;
        ctx.strokeStyle = '#44ffff'; // cyan
        ctx.beginPath();
        ctx.moveTo(0, sy);
        ctx.lineTo(this.canvas.width, sy);
        ctx.stroke();
      }
    });
    ctx.setLineDash([]);
  }

  // ─── ROAD HELPERS ───

  _segmentNormal(segments, idx) {
    let dx, dz;
    if (segments.length < 2) return { nx: 0, nz: 1 };
    if (idx === 0) {
      dx = segments[1].x - segments[0].x;
      dz = segments[1].z - segments[0].z;
    } else if (idx >= segments.length - 1) {
      dx = segments[segments.length - 1].x - segments[segments.length - 2].x;
      dz = segments[segments.length - 1].z - segments[segments.length - 2].z;
    } else {
      dx = segments[idx + 1].x - segments[idx - 1].x;
      dz = segments[idx + 1].z - segments[idx - 1].z;
    }
    const len = Math.hypot(dx, dz) || 1;
    return { nx: -dz / len, nz: dx / len };
  }

  _catmullRomTessellate(points, segsPerSpan) {
    if (points.length < 2) return [...points];
    if (points.length === 2 || !segsPerSpan) return [...points];
    const result = [];
    const n = points.length;
    for (let i = 0; i < n - 1; i++) {
      const p0 = points[Math.max(i - 1, 0)];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = points[Math.min(i + 2, n - 1)];
      for (let s = 0; s < segsPerSpan; s++) {
        const t = s / segsPerSpan;
        const t2 = t * t, t3 = t2 * t;
        const x = 0.5 * ((2 * p1.x) + (-p0.x + p2.x) * t + (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 + (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3);
        const z = 0.5 * ((2 * p1.z) + (-p0.z + p2.z) * t + (2 * p0.z - 5 * p1.z + 4 * p2.z - p3.z) * t2 + (-p0.z + 3 * p1.z - 3 * p2.z + p3.z) * t3);
        result.push({ x, z });
      }
    }
    result.push(points[n - 1]);
    return result;
  }

  _polylineLength(segments) {
    let len = 0;
    for (let i = 1; i < segments.length; i++) {
      len += Math.hypot(segments[i].x - segments[i - 1].x, segments[i].z - segments[i - 1].z);
    }
    return len;
  }

  _pointAtDistance(segments, dist) {
    let acc = 0;
    for (let i = 1; i < segments.length; i++) {
      const dx = segments[i].x - segments[i - 1].x;
      const dz = segments[i].z - segments[i - 1].z;
      const segLen = Math.hypot(dx, dz);
      if (acc + segLen >= dist) {
        const t = (dist - acc) / segLen;
        return {
          x: segments[i - 1].x + dx * t,
          z: segments[i - 1].z + dz * t,
          angle: Math.atan2(dx, dz),
        };
      }
      acc += segLen;
    }
    const last = segments[segments.length - 1];
    const prev = segments[segments.length - 2] || last;
    return { x: last.x, z: last.z, angle: Math.atan2(last.x - prev.x, last.z - prev.z) };
  }

  // ─── ROAD DRAWING ───

  _drawPlacedRoads() {
    const ctx = this.ctx;
    this.roads.forEach((rd, i) => {
      const pts = rd.points;
      if (!pts || pts.length < 2) return;
      const isSelected = this.editingRoads && i === this.selectedRoadIndex;
      const segments = rd.curved ? this._catmullRomTessellate(pts, 16) : pts;

      // Compute road polygon edges
      const leftEdge = [], rightEdge = [];
      const hw = (rd.width || 30) / 2;
      for (let j = 0; j < segments.length; j++) {
        const { nx, nz } = this._segmentNormal(segments, j);
        leftEdge.push({ x: segments[j].x + nx * hw, z: segments[j].z + nz * hw });
        rightEdge.push({ x: segments[j].x - nx * hw, z: segments[j].z - nz * hw });
      }

      // Draw filled road polygon
      ctx.globalAlpha = isSelected ? 0.3 : 0.15;
      ctx.fillStyle = '#888';
      ctx.beginPath();
      leftEdge.forEach((p, j) => {
        const s = this._toScreen(p.x, p.z);
        j === 0 ? ctx.moveTo(s.x, s.y) : ctx.lineTo(s.x, s.y);
      });
      const re = [...rightEdge].reverse();
      re.forEach(p => {
        const s = this._toScreen(p.x, p.z);
        ctx.lineTo(s.x, s.y);
      });
      ctx.closePath();
      ctx.fill();
      ctx.globalAlpha = 1;

      // Footpath strips (lighter fill at each edge)
      const fpW = rd.footpathWidth || 0;
      if (fpW > 0) {
        ctx.fillStyle = 'rgba(180,175,165,0.35)';
        // Left footpath
        ctx.beginPath();
        for (let j = 0; j < segments.length; j++) {
          const { nx, nz } = this._segmentNormal(segments, j);
          const s = this._toScreen(segments[j].x + nx * hw, segments[j].z + nz * hw);
          j === 0 ? ctx.moveTo(s.x, s.y) : ctx.lineTo(s.x, s.y);
        }
        for (let j = segments.length - 1; j >= 0; j--) {
          const { nx, nz } = this._segmentNormal(segments, j);
          const s = this._toScreen(segments[j].x + nx * (hw - fpW), segments[j].z + nz * (hw - fpW));
          ctx.lineTo(s.x, s.y);
        }
        ctx.closePath();
        ctx.fill();
        // Right footpath
        ctx.beginPath();
        for (let j = 0; j < segments.length; j++) {
          const { nx, nz } = this._segmentNormal(segments, j);
          const s = this._toScreen(segments[j].x - nx * hw, segments[j].z - nz * hw);
          j === 0 ? ctx.moveTo(s.x, s.y) : ctx.lineTo(s.x, s.y);
        }
        for (let j = segments.length - 1; j >= 0; j--) {
          const { nx, nz } = this._segmentNormal(segments, j);
          const s = this._toScreen(segments[j].x - nx * (hw - fpW), segments[j].z - nz * (hw - fpW));
          ctx.lineTo(s.x, s.y);
        }
        ctx.closePath();
        ctx.fill();
      }

      // Yellow edge stripes
      const stripeW = rd.edgeStripeWidth || 0;
      if (stripeW > 0) {
        const stripeOff = hw - fpW;
        ctx.strokeStyle = '#dddd22';
        ctx.lineWidth = Math.max(1.5, stripeW * this.zoom);
        ctx.setLineDash([]);
        // Left stripe
        ctx.beginPath();
        for (let j = 0; j < segments.length; j++) {
          const { nx, nz } = this._segmentNormal(segments, j);
          const s = this._toScreen(segments[j].x + nx * stripeOff, segments[j].z + nz * stripeOff);
          j === 0 ? ctx.moveTo(s.x, s.y) : ctx.lineTo(s.x, s.y);
        }
        ctx.stroke();
        // Right stripe
        ctx.beginPath();
        for (let j = 0; j < segments.length; j++) {
          const { nx, nz } = this._segmentNormal(segments, j);
          const s = this._toScreen(segments[j].x - nx * stripeOff, segments[j].z - nz * stripeOff);
          j === 0 ? ctx.moveTo(s.x, s.y) : ctx.lineTo(s.x, s.y);
        }
        ctx.stroke();
      }

      // Border
      if (isSelected) {
        ctx.strokeStyle = '#ffcc00';
        ctx.lineWidth = 1.5;
      } else {
        ctx.strokeStyle = 'rgba(136,136,136,0.3)';
        ctx.lineWidth = 1;
      }
      ctx.beginPath();
      leftEdge.forEach((p, j) => {
        const s = this._toScreen(p.x, p.z);
        j === 0 ? ctx.moveTo(s.x, s.y) : ctx.lineTo(s.x, s.y);
      });
      ctx.stroke();
      ctx.beginPath();
      rightEdge.forEach((p, j) => {
        const s = this._toScreen(p.x, p.z);
        j === 0 ? ctx.moveTo(s.x, s.y) : ctx.lineTo(s.x, s.y);
      });
      ctx.stroke();

      // Centerline dashed
      ctx.strokeStyle = isSelected ? '#ffcc00' : '#aaa';
      ctx.setLineDash([6, 4]);
      ctx.lineWidth = 1;
      ctx.beginPath();
      segments.forEach((p, j) => {
        const s = this._toScreen(p.x, p.z);
        j === 0 ? ctx.moveTo(s.x, s.y) : ctx.lineTo(s.x, s.y);
      });
      ctx.stroke();
      ctx.setLineDash([]);

      // Median planters (green rects along centerline)
      if (rd.medianPlanters) {
        this._drawRoadMedianPlanters(rd, segments);
      }

      // Label at midpoint
      const totalLen = this._polylineLength(segments);
      const mid = this._pointAtDistance(segments, totalLen / 2);
      const midS = this._toScreen(mid.x, mid.z);
      const angle = mid.angle;
      ctx.save();
      ctx.translate(midS.x, midS.y);
      ctx.rotate(-angle);
      ctx.fillStyle = isSelected ? '#ffcc00' : '#bbb';
      ctx.font = 'bold 10px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      ctx.fillText(rd.label || rd.id, 0, -4);
      ctx.restore();

      // Measurements when selected
      if (isSelected) {
        this._drawRoadMeasurements(rd, i);
      }

      // Control point handles (when editing)
      if (this.editingRoads) {
        pts.forEach((p, pi) => {
          const s = this._toScreen(p.x, p.z);
          const isCtrlSelected = isSelected;
          ctx.fillStyle = isCtrlSelected ? '#ffcc00' : '#aa8800';
          ctx.beginPath();
          ctx.arc(s.x, s.y, 5, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = '#000';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.arc(s.x, s.y, 5, 0, Math.PI * 2);
          ctx.stroke();

          // Point index label
          if (isSelected) {
            ctx.fillStyle = '#ffcc00';
            ctx.font = '8px sans-serif';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'bottom';
            ctx.fillText(`${pi}`, s.x, s.y - 7);
          }
        });
      }
    });
  }

  _drawRoadMedianPlanters(rd, segments) {
    const ctx = this.ctx;
    const totalLen = this._polylineLength(segments);
    const roadW = rd.width || 200;
    const planterW = rd.medianPlanterWidthPct
      ? (rd.medianPlanterWidthPct / 100) * roadW
      : (rd.medianPlanterWidth || 30);
    const gap = rd.medianPlanterGapPct
      ? (rd.medianPlanterGapPct / 100) * roadW
      : ((rd.medianPlanterSpacing || 40) - (rd.medianPlanterWidth || 30));
    const spacing = planterW + gap;
    const w = planterW * this.zoom;
    const d = (rd.medianPlanterDepth || 8) * this.zoom;
    let dist = spacing / 2;
    while (dist < totalLen - spacing / 4) {
      const pt = this._pointAtDistance(segments, dist);
      const sc = this._toScreen(pt.x, pt.z);
      const ang = pt.angle + (rd.medianPlanterAngle || 0) * Math.PI / 180;
      ctx.save();
      ctx.translate(sc.x, sc.y);
      ctx.rotate(-ang);
      ctx.fillStyle = 'rgba(42,138,42,0.5)';
      ctx.fillRect(-w / 2, -d / 2, w, d);
      ctx.strokeStyle = 'rgba(42,138,42,0.7)';
      ctx.lineWidth = 1;
      ctx.strokeRect(-w / 2, -d / 2, w, d);
      // Tree dot
      if (rd.medianHasTrees) {
        ctx.fillStyle = '#1a5a1a';
        ctx.beginPath();
        ctx.arc(0, 0, 3, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
      dist += spacing;
    }
  }

  // ─── ROAD INTERACTION ───

  _hitTestRoad(bx, bz) {
    for (let ri = this.roads.length - 1; ri >= 0; ri--) {
      const rd = this.roads[ri];
      const pts = rd.points;
      if (!pts || pts.length < 2) continue;
      const segments = rd.curved ? this._catmullRomTessellate(pts, 16) : pts;
      const hw = (rd.width || 30) / 2;
      // Check if point is within road width of any segment
      for (let j = 0; j < segments.length - 1; j++) {
        const dist = this._pointToSegmentDist(bx, bz, segments[j].x, segments[j].z, segments[j + 1].x, segments[j + 1].z);
        if (dist <= hw) return ri;
      }
    }
    return -1;
  }

  _addRoad() {
    const center = this._toBuilding(this.canvas.width / 2, this.canvas.height / 2);
    const id = 'RD-' + (this.roads.length + 1);
    this.roads.push({
      id, label: 'Road ' + (this.roads.length + 1),
      points: [
        { x: this._snap(center.x - 100), z: this._snap(center.z) },
        { x: this._snap(center.x + 100), z: this._snap(center.z) },
      ],
      width: 100,
      curved: false,
      medianPlanters: false,
      edgeStripeWidth: 0,
      footpathWidth: 0,
    });
    this.selectedRoadIndex = this.roads.length - 1;
    this._saveSnapshot();
    this._updateRoadPanel();
  }

  // ─── ROAD PANEL ───

  _updateRoadPanel() {
    if (!this.editingRoads) return;

    if (this.selectedRoadIndex < 0) {
      this.panel.innerHTML = `<h3>Roads</h3>
        <p class="ed-hint">Click a road to select it. ${this.roads.length} roads total.</p>`;
      return;
    }

    const rd = this.roads[this.selectedRoadIndex];
    if (!rd) return;

    let html = `<h3>Road: ${rd.label}</h3>
      <div class="ed-field">
        <label>Label</label>
        <input type="text" id="ed-rd-label" value="${rd.label || ''}" />
      </div>
      <div class="ed-field">
        <label>Width (ft)</label>
        <input type="number" id="ed-rd-width" value="${rd.width || 30}" min="10" max="500" step="5" />
      </div>
      <div class="ed-field">
        <label>Curved</label>
        <input type="checkbox" id="ed-rd-curved" ${rd.curved ? 'checked' : ''} />
      </div>
      <hr style="border-color:#444;margin:8px 0"/>
      <div class="ed-field">
        <label>Median Planters</label>
        <input type="checkbox" id="ed-rd-median" ${rd.medianPlanters ? 'checked' : ''} />
      </div>`;

    if (rd.medianPlanters) {
      const curWidthPct = rd.medianPlanterWidthPct || ((rd.medianPlanterWidth || 30) / (rd.width || 200) * 100);
      const curGapPct = rd.medianPlanterGapPct || (((rd.medianPlanterSpacing || 40) - (rd.medianPlanterWidth || 30)) / (rd.width || 200) * 100);
      html += `
      <div class="ed-field">
        <label>Planter W (%)</label>
        <input type="number" id="ed-rd-pwpct" value="${curWidthPct.toFixed(1)}" min="1" max="50" step="0.5" />
      </div>
      <div class="ed-field">
        <label>Gap (%)</label>
        <input type="number" id="ed-rd-pgpct" value="${curGapPct.toFixed(1)}" min="0.5" max="50" step="0.5" />
      </div>
      <div class="ed-field">
        <label>Planter D (ft)</label>
        <input type="number" class="ed-rdm" data-prop="medianPlanterDepth" value="${rd.medianPlanterDepth || 8}" min="2" step="1" />
      </div>
      <div class="ed-field">
        <label>Planter H (ft)</label>
        <input type="number" class="ed-rdm" data-prop="medianPlanterHeight" value="${rd.medianPlanterHeight || 3}" min="1" step="0.5" />
      </div>
      <div class="ed-field">
        <label>Rotation (deg)</label>
        <input type="number" class="ed-rdm" data-prop="medianPlanterAngle" value="${rd.medianPlanterAngle || 0}" step="1" />
      </div>
      <div class="ed-field">
        <label>Has Trees</label>
        <input type="checkbox" id="ed-rd-trees" ${rd.medianHasTrees ? 'checked' : ''} />
      </div>`;
    }

    html += `<hr style="border-color:#444;margin:8px 0"/>
      <div style="font-size:10px;color:#aaa;margin-bottom:4px;font-weight:bold">Edge Stripes &amp; Footpath</div>
      <div class="ed-field">
        <label>Stripe W (ft)</label>
        <input type="number" id="ed-rd-stripe" value="${rd.edgeStripeWidth || 0}" min="0" max="5" step="0.25" />
      </div>
      <div class="ed-field">
        <label>Footpath W (ft)</label>
        <input type="number" id="ed-rd-footpath" value="${rd.footpathWidth || 0}" min="0" max="30" step="1" />
      </div>
      <hr style="border-color:#444;margin:8px 0"/>
      <div style="font-size:10px;color:#999;margin-bottom:4px">Control Points (right-click to add/remove)</div>
      <div class="ed-vertex-list" id="ed-road-pts">
        ${rd.points.map((p, pi) => `
          <div class="ed-vertex-row">
            <span class="ed-vertex-idx">${pi}</span>
            <input type="number" class="ed-vertex-input ed-rdpt" data-pi="${pi}" data-axis="x" value="${p.x.toFixed(1)}" step="0.5" />
            <input type="number" class="ed-vertex-input ed-rdpt" data-pi="${pi}" data-axis="z" value="${p.z.toFixed(1)}" step="0.5" />
          </div>
        `).join('')}
      </div>
      <div class="ed-field ed-readonly">
        <label>ID</label>
        <span>${rd.id}</span>
      </div>`;

    this.panel.innerHTML = html;

    // Wire inputs
    const labelEl = document.getElementById('ed-rd-label');
    if (labelEl) {
      labelEl.addEventListener('input', () => { rd.label = labelEl.value; });
      labelEl.addEventListener('change', () => this._saveSnapshot());
    }
    const widthEl = document.getElementById('ed-rd-width');
    if (widthEl) {
      widthEl.addEventListener('input', () => { rd.width = parseFloat(widthEl.value) || 30; });
      widthEl.addEventListener('change', () => this._saveSnapshot());
    }
    const curvedEl = document.getElementById('ed-rd-curved');
    if (curvedEl) {
      curvedEl.addEventListener('change', () => {
        rd.curved = curvedEl.checked;
        this._saveSnapshot();
      });
    }
    const medianEl = document.getElementById('ed-rd-median');
    if (medianEl) {
      medianEl.addEventListener('change', () => {
        rd.medianPlanters = medianEl.checked;
        if (rd.medianPlanters && !rd.medianPlanterWidthPct) {
          rd.medianPlanterWidthPct = 15;
          rd.medianPlanterGapPct = 5;
          rd.medianPlanterDepth = 8;
          rd.medianPlanterHeight = 3;
          rd.medianHasTrees = true;
        }
        this._saveSnapshot();
        this._updateRoadPanel();
      });
    }
    const treesEl = document.getElementById('ed-rd-trees');
    if (treesEl) {
      treesEl.addEventListener('change', () => {
        rd.medianHasTrees = treesEl.checked;
        this._saveSnapshot();
      });
    }
    // Planter width % input
    const pwPctEl = document.getElementById('ed-rd-pwpct');
    if (pwPctEl) {
      pwPctEl.addEventListener('input', () => {
        rd.medianPlanterWidthPct = parseFloat(pwPctEl.value) || 15;
        rd.medianPlanterWidth = (rd.medianPlanterWidthPct / 100) * (rd.width || 200);
      });
      pwPctEl.addEventListener('change', () => this._saveSnapshot());
    }
    // Planter gap % input
    const pgPctEl = document.getElementById('ed-rd-pgpct');
    if (pgPctEl) {
      pgPctEl.addEventListener('input', () => {
        rd.medianPlanterGapPct = parseFloat(pgPctEl.value) || 5;
        const planterW = (rd.medianPlanterWidthPct || 15) / 100 * (rd.width || 200);
        const gap = (rd.medianPlanterGapPct / 100) * (rd.width || 200);
        rd.medianPlanterSpacing = planterW + gap;
      });
      pgPctEl.addEventListener('change', () => this._saveSnapshot());
    }
    // Edge stripe width
    const stripeEl = document.getElementById('ed-rd-stripe');
    if (stripeEl) {
      stripeEl.addEventListener('input', () => { rd.edgeStripeWidth = parseFloat(stripeEl.value) || 0; });
      stripeEl.addEventListener('change', () => this._saveSnapshot());
    }
    // Footpath width
    const footpathEl = document.getElementById('ed-rd-footpath');
    if (footpathEl) {
      footpathEl.addEventListener('input', () => { rd.footpathWidth = parseFloat(footpathEl.value) || 0; });
      footpathEl.addEventListener('change', () => this._saveSnapshot());
    }
    document.querySelectorAll('.ed-rdm').forEach(inp => {
      inp.addEventListener('input', () => {
        rd[inp.dataset.prop] = parseFloat(inp.value) || 0;
      });
      inp.addEventListener('change', () => this._saveSnapshot());
    });
    document.querySelectorAll('.ed-rdpt').forEach(inp => {
      inp.addEventListener('input', () => {
        const pi = parseInt(inp.dataset.pi);
        const axis = inp.dataset.axis;
        if (rd.points[pi]) rd.points[pi][axis] = parseFloat(inp.value) || 0;
      });
      inp.addEventListener('change', () => this._saveSnapshot());
    });
  }

  _updateRoadPanelValues() {
    if (this.selectedRoadIndex < 0) return;
    const rd = this.roads[this.selectedRoadIndex];
    if (!rd) return;
    document.querySelectorAll('.ed-rdpt').forEach(inp => {
      const pi = parseInt(inp.dataset.pi);
      const axis = inp.dataset.axis;
      if (rd.points[pi]) inp.value = rd.points[pi][axis].toFixed(1);
    });
  }

  // ─── APPLY / EXPORT ───

  _applyToModel() {
    const deepCopy = (arr) => JSON.parse(JSON.stringify(arr));

    // Update building outline
    FOOTPRINT_OUTER.length = 0;
    deepCopy(this.outlineVertices).forEach(v => FOOTPRINT_OUTER.push(v));

    // Update walkway outline
    WALKWAY_OUTLINE.length = 0;
    deepCopy(this.walkwayVertices).forEach(v => WALKWAY_OUTLINE.push(v));

    // Update stair outline
    STAIR_OUTLINE.length = 0;
    deepCopy(this.stairVertices).forEach(v => STAIR_OUTLINE.push(v));
    setStairNumSteps(this.stairNumSteps);

    // Recompute BUILDING dimensions from outline AABB
    const aabb = this._computeAABB(this.outlineVertices);
    BUILDING.width = aabb.w;
    BUILDING.depth = aabb.d;

    // Try to detect L-cutout: find the vertex that forms the inner corner
    // The inner corner is a vertex where both adjacent edges go toward the bounding box edges
    const verts = this.outlineVertices;
    let foundCut = false;
    for (let i = 0; i < verts.length; i++) {
      const prev = verts[(i - 1 + verts.length) % verts.length];
      const curr = verts[i];
      const next = verts[(i + 1) % verts.length];
      // Inner corner: both neighbors are on the bounding box edge but this vertex is interior
      const onMinX = Math.abs(curr.x - aabb.x) < 1;
      const onMinZ = Math.abs(curr.z - aabb.z) < 1;
      const onMaxX = Math.abs(curr.x - (aabb.x + aabb.w)) < 1;
      const onMaxZ = Math.abs(curr.z - (aabb.z + aabb.d)) < 1;
      const onEdge = (onMinX || onMaxX) && (onMinZ || onMaxZ);
      if (!onEdge && (Math.abs(prev.x - curr.x) < 1 || Math.abs(prev.z - curr.z) < 1)) {
        // This is an interior vertex — likely the L-cutout corner
        BUILDING.cutX = curr.x - aabb.x;
        BUILDING.cutZ = curr.z - aabb.z;
        foundCut = true;
        break;
      }
    }
    if (!foundCut) {
      BUILDING.cutX = 0;
      BUILDING.cutZ = 0;
    }

    // Update floor data
    GROUND_FLOOR.showrooms.length = 0;
    deepCopy(this.data.ground.showrooms).forEach(s => GROUND_FLOOR.showrooms.push(s));

    GROUND_FLOOR.specialAreas.length = 0;
    deepCopy(this.data.ground.specialAreas).forEach(s => GROUND_FLOOR.specialAreas.push(s));

    UPPER_FLOOR.showrooms.length = 0;
    deepCopy(this.data.upper.showrooms).forEach(s => UPPER_FLOOR.showrooms.push(s));

    UPPER_FLOOR.offices.length = 0;
    deepCopy(this.data.upper.offices).forEach(s => UPPER_FLOOR.offices.push(s));

    // Update exterior data
    GROUND_PAD_OUTLINE.length = 0;
    deepCopy(this.groundPadVertices).forEach(v => GROUND_PAD_OUTLINE.push(v));

    PARKING_GROUPS.length = 0;
    deepCopy(this.parkingGroups).forEach(g => PARKING_GROUPS.push(g));

    PLACED_PLANTERS.length = 0;
    deepCopy(this.planters).forEach(p => PLACED_PLANTERS.push(p));

    PLACED_ROADS.length = 0;
    deepCopy(this.roads).forEach(r => PLACED_ROADS.push(r));

    ITEM_GROUPS.length = 0;
    deepCopy(this.groups).forEach(g => ITEM_GROUPS.push(g));

    if (this.onApply) this.onApply();
  }

  _exportJSON() {
    const payload = {
      outline: this.outlineVertices,
      walkway: this.walkwayVertices,
      stair: this.stairVertices,
      stairNumSteps: this.stairNumSteps,
      groundPad: this.groundPadVertices,
      parkingGroups: this.parkingGroups,
      planters: this.planters,
      roads: this.roads,
      groups: this.groups,
      groundFloor: {
        showrooms: this.data.ground.showrooms,
        specialAreas: this.data.ground.specialAreas,
      },
      upperFloor: {
        showrooms: this.data.upper.showrooms,
        offices: this.data.upper.offices,
      },
    };
    const json = JSON.stringify(payload, null, 2);
    navigator.clipboard.writeText(json).then(() => {
      const btn = document.getElementById('ed-export');
      const orig = btn.textContent;
      btn.textContent = 'Copied!';
      setTimeout(() => { btn.textContent = orig; }, 1500);
    });
  }
}
